import React, { useMemo, useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
// Bootstrap removed — styled with CSS variables
import { toast } from 'react-toastify';
import api, { resolveApiErrorMessage } from '../../services/axios';
import { ENDPOINTS } from '../../config/endpoints';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import OwnTemplateFlow from './OwnTemplateFlow';
import { downloadStandardizedPdf, base64ToBlob, triggerBlobDownload } from '../../utils/companyResumePdf';

// ── Hardcoded format templates (Feuji1/Feuji2/Feuji3) ────────────────────────
const TEMPLATE_FALLBACK = {
  feuji1: { id: 'feuji1', display_name: 'Feuji1', description: 'Orange header bar, Feuji logo, Arial body, label/colon/value skill rows' },
  feuji2: { id: 'feuji2', display_name: 'Feuji2', description: 'Clean centered name + subtitle, tabbed Education rows, inline bold-category skills, justified text' },
  feuji3: { id: 'feuji3', display_name: 'Feuji3', description: 'Dense layout: long Summary lines, inline Key Skills, Environment/Technologies per role' },
  feuji4: { id: 'feuji4', display_name: 'Feuji4', description: 'Full-width orange top bar with right-aligned logo on every page, dense Arial 9pt justified body, tab-aligned skill rows' },
};

const TEMPLATE_MOCKUPS = {
  feuji1: `<!DOCTYPE html><html><head><style>
    body{font-family:Arial,sans-serif;margin:0;background:#fff;}
    .bar{height:26pt;background:#F3762D;display:flex;align-items:center;justify-content:flex-end;padding:0 18pt;}
    .bar span{color:#fff;font-size:10pt;font-weight:bold;}
    .wrap{padding:14pt 40pt;}
    h1{font-size:14pt;margin:6pt 0;}
    h2{font-size:12pt;text-decoration:underline;color:#E06018;margin:12pt 0 4pt;}
    .row{display:flex;margin:2pt 0 2pt 14pt;font-size:10.5pt;}
    .row b{width:110pt;} .row .c{width:12pt;}
  </style></head><body>
    <div class="bar"><span>feuji</span></div>
    <div class="wrap">
      <h1 style="text-align:left;">CANDIDATE NAME</h1>
      <h2>Professional Summary:</h2>
      <div class="row" style="margin-left:0;">Senior engineer with 8+ years building scalable systems.</div>
      <h2>Technical Skills:</h2>
      <div class="row"><b>Languages</b><span class="c">:</span><span>Java, Python, SQL</span></div>
      <div class="row"><b>Cloud</b><span class="c">:</span><span>AWS, Kubernetes, Docker</span></div>
      <h2>Projects:</h2>
      <div class="row" style="margin-left:0;"><i>InstantSettle — realtime settlement engine</i></div>
    </div>
  </body></html>`,
  feuji2: `<!DOCTYPE html><html><head><style>
    body{font-family:Arial,sans-serif;margin:0;background:#fff;}
    .wrap{padding:18pt 40pt;}
    .logo{width:99pt;display:block;margin:0 0 6pt;}
    h1{font-size:13pt;text-align:center;margin:0 0 2pt;}
    .sub{font-size:10pt;font-weight:bold;text-align:center;margin-bottom:6pt;}
    hr{border:none;border-top:1px solid #000;margin:4pt 0 10pt;}
    h2{font-size:10pt;margin:10pt 0 3pt;}
    p{font-size:10pt;text-align:justify;margin:0 0 4pt;line-height:1.3;}
    .row{display:flex;font-size:10pt;margin-bottom:3pt;}
    .row .a{width:38%;} .row .b{flex:1;text-align:center;} .row .c{width:18%;text-align:right;}
    .erow{display:flex;justify-content:space-between;font-weight:bold;font-size:10pt;margin:8pt 0 3pt;}
    .bul{display:flex;margin:0 0 2pt 0;padding-left:14pt;font-size:10pt;}
    .bul span:first-child{width:11pt;flex-shrink:0;}
    .bul span:last-child{flex:1;text-align:justify;}
  </style></head><body>
    <div class="wrap">
      <img class="logo" src="/feuji-logo-alt.png"/>
      <h1>CANDIDATE NAME</h1>
      <div class="sub">Senior Software Engineer</div>
      <hr/>
      <h2>Professional Summary:</h2>
      <p>Senior engineer with 8+ years building scalable, cloud-native systems.</p>
      <h2>Education:</h2>
      <div class="row"><span class="a">B.Tech, Computer Science</span><span class="b">State University</span><span class="c">2015 - 2019</span></div>
      <h2>Core Competencies:</h2>
      <p>Distributed Systems, API Design, ML Platforms, Team Leadership</p>
      <h2>Technical Skills:</h2>
      <p><b>Programming:</b> Python, Java, TypeScript, SQL</p>
      <p><b>Cloud &amp; DevOps:</b> AWS, Docker, Kubernetes, Jenkins</p>
      <h2>Professional Experience:</h2>
      <div class="erow"><span>Google &mdash; New York, NY</span><span>2021 - Present</span></div>
      <div style="font-weight:bold;font-size:10pt;margin-bottom:3pt;">Senior Software Engineer</div>
      <div class="bul"><span>&bull;</span><span>Architected enterprise-grade services.</span></div>
      <div class="bul"><span>&bull;</span><span>Partnered with product and design teams.</span></div>
    </div>
  </body></html>`,
  feuji3: `<!DOCTYPE html><html><head><style>
    body{font-family:Arial,sans-serif;margin:0;background:#fff;}
    .wrap{padding:16pt 34pt;}
    .logo{width:99pt;display:block;margin:0 0 6pt;}
    h1{font-size:12pt;text-align:center;margin:0 0 6pt;}
    h2{font-size:10pt;margin:8pt 0 2pt;}
    p{font-size:10pt;text-align:justify;margin:0 0 2pt;line-height:1.15;}
    .erow{display:flex;justify-content:space-between;font-weight:bold;font-size:10pt;margin:8pt 0 2pt;}
    .bul{display:flex;margin:0 0 2pt 0;padding-left:12pt;font-size:10pt;}
    .bul span:first-child{width:10pt;flex-shrink:0;}
    .bul span:last-child{flex:1;text-align:justify;}
  </style></head><body>
    <div class="wrap">
      <img class="logo" src="/feuji-logo-alt.png"/>
      <h1>CANDIDATE NAME</h1>
      <h2>Summary:</h2>
      <p>16+ years of IT experience across data platforms, modeling and analytics.</p>
      <p>Deep expertise in SQL, ETL architecture and cloud warehousing.</p>
      <h2>Key Skills:</h2>
      <p><b>Databases:</b> SQL Server, PostgreSQL, Snowflake</p>
      <p><b>ETL Tools:</b> SSIS, Azure Data Factory, Informatica</p>
      <h2>Education:</h2>
      <p>MSc in Information Technology - Alagappa University</p>
      <h2>Certificates:</h2>
      <p>MCDBA - Microsoft Certified Database Administrator</p>
      <h2>Professional Experience:</h2>
      <div class="erow"><span>LOSP, Charlotte, NC</span><span>2019 - 2022</span></div>
      <div style="font-weight:bold;font-size:10pt;margin-bottom:2pt;">(Data Migration - Fidelity National)</div>
      <div style="font-weight:bold;font-size:10pt;margin-bottom:2pt;">Data Architect / Modeler / Lead</div>
      <div class="bul"><span>&bull;</span><span>Designed dimensional models for enterprise reporting.</span></div>
      <div class="bul"><span>&bull;</span><span>Developed and supported ETL architectures.</span></div>
      <p><b>Environment / Technologies:</b> SQL Server, SSIS, Azure Data Factory</p>
    </div>
  </body></html>`,
  feuji4: `<!DOCTYPE html><html><head><style>
    body{font-family:Arial,sans-serif;margin:0;background:#fff;}
    .bar{background:#EC7C30;display:flex;justify-content:flex-end;align-items:center;padding:6pt 20pt;}
    .bar img{height:19pt;width:auto;}
    .wrap{padding:0 30pt 20pt 30pt;}
    h2{font-size:9pt;margin:8pt 0 2pt;text-decoration:underline;}
    .name{font-weight:bold;font-size:10pt;margin:4pt 0 6pt;}
    p{font-size:9pt;text-align:justify;margin:0 0 2pt;line-height:1.25;}
    .bul{display:flex;font-size:9pt;margin:0 0 2pt;padding-left:14pt;line-height:1.25;}
    .bul span:first-child{min-width:11pt;}
    .bul div{flex:1;text-align:justify;}
    .lbl{display:flex;font-weight:bold;font-size:9pt;margin-bottom:1pt;}
    .lbl span:first-child{width:70pt;flex-shrink:0;}
  </style></head><body>
    <div class="bar"><img src="/feuji-logo-header.png"/></div>
    <div class="wrap">
      <div class="name">CANDIDATE NAME</div>
      <h2>Professional Summary:</h2>
      <div class="bul"><span>&bull;</span><div>DevOps engineer with deep AWS, Kubernetes and CI/CD expertise.</div></div>
      <h2>Technical Skill:</h2>
      <div class="bul"><span>&bull;</span><div>Cloud Services&nbsp;&nbsp;: AWS (EC2, S3, Lambda), Azure</div></div>
      <div class="bul"><span>&bull;</span><div>CI/CD tools&nbsp;&nbsp;: Jenkins, GitHub Actions, CodePipeline</div></div>
      <h2>Certifications:</h2>
      <div class="bul"><span>&bull;</span><div>AWS Certified Solutions Architect</div></div>
      <h2>Professional Experience:</h2>
      <div class="lbl"><span>Role</span><span>: Lead Developer</span></div>
      <div class="lbl"><span>Company</span><span>: ACME Corp</span></div>
      <div class="lbl"><span>Duration</span><span>: Jan 2022 – Present</span></div>
      <div class="lbl"><span>Environment</span><span>: AWS EKS, Terraform, Ansible</span></div>
      <div style="font-weight:bold;font-size:9pt;margin:2pt 0;">Responsibilities:</div>
      <div class="bul"><span>&bull;</span><div>Created monitoring and automated deployment pipelines.</div></div>
    </div>
  </body></html>`,
};

function TemplatePreviewModal({ template, onClose }) {
  if (!template) return null;
  return createPortal(
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px 12px', paddingLeft: 'calc(var(--app-sidebar-width, 0px) + 12px)', overflowY: 'auto' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: 'rgba(0,0,0,0.55)', position: 'fixed', inset: 0 }} onClick={onClose} />
      <div style={{ width: '100%', maxWidth: 860, background: 'var(--ds-card)', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.4)', overflow: 'hidden', position: 'relative' }}>
        <div style={{ padding: '12px 16px', background: 'linear-gradient(135deg,#0041C2,#1A5FD4)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#fff' }}>{template.display_name} — Template Preview</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.78)', maxWidth: 640 }}>{template.description}</div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: 28, height: 28, borderRadius: 6, cursor: 'pointer', outline: 'none' }}>
            <i className="fas fa-times" />
          </button>
        </div>
        <div style={{ padding: 14, background: '#eef1f5' }}>
          <iframe
            title={`template-preview-${template.id}`}
            srcDoc={TEMPLATE_MOCKUPS[template.id] || '<p>No preview available</p>'}
            sandbox="allow-same-origin allow-scripts"
            style={{ width: '100%', height: '58vh', border: 'none', borderRadius: 8, background: '#fff' }}
          />
        </div>
        <div style={{ padding: '10px 16px', borderTop: '1px solid var(--ds-border)', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '7px 14px', fontSize: 12, fontWeight: 700, borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', border: '1.5px solid var(--ds-border)', background: 'var(--ds-input-bg)', color: 'var(--ds-text)' }}>
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/**
 * Full HTML documents from the API break when injected into a div (styles in head may not apply).
 * Merge head styles with body innerHTML for a fragment the browser renders reliably.
 */
function extractResumePreviewHtml(htmlString) {
  if (!htmlString || typeof htmlString !== 'string') return htmlString;
  const trimmed = htmlString.trim();
  if (!/^<!DOCTYPE|^<html[\s>]/i.test(trimmed)) return htmlString;
  try {
    const doc = new DOMParser().parseFromString(trimmed, 'text/html');
    const styles = Array.from(doc.querySelectorAll('head style'))
      .map((el) => el.textContent || '')
      .join('\n');
    const bodyHtml = doc.body ? doc.body.innerHTML : trimmed;
    return styles ? `<style>${styles}</style>${bodyHtml}` : bodyHtml;
  } catch {
    return htmlString;
  }
}

function isFullHtmlDocument(htmlString) {
  if (!htmlString || typeof htmlString !== 'string') return false;
  const trimmed = htmlString.trim();
  return /^<!DOCTYPE|^<html[\s>]/i.test(trimmed);
}

function parseHtmlDocument(htmlString) {
  try {
    return new DOMParser().parseFromString(String(htmlString || ''), 'text/html');
  } catch {
    return null;
  }
}

const PLACEHOLDER_FIELD_VALUES = new Set([
  '-', '.', '—', '–', '--', '---',
  'n/a', 'na', 'n.a.', 'n.a',
  'none', 'null', 'undefined',
  'not available', 'not applicable',
  'not specified', 'not mentioned',
  'not provided', 'not given',
  'not listed', 'not stated',
  'unknown', 'no data', 'no info',
  'tbd', 'tba', 'nil', 'no',
  'not found', 'not present',
  'no certifications', 'no achievements',
  'no education', 'no experience',
]);

function hasDisplayValue(val) {
  if (val == null) return false;
  const s = String(val).replace(/\s+/g, ' ').trim();
  if (!s) return false;
  const low = s.toLowerCase().replace(/\.$/, '');
  if (PLACEHOLDER_FIELD_VALUES.has(low)) return false;
  if (/^[-–—./\\]+$/.test(s)) return false;
  return true;
}

/** Remove label/value rows (and related blocks) that only contain placeholders like "-" */
function removeEmptyResumeFieldRows(root) {
  if (!root) return;

  root.querySelectorAll('.align-row').forEach((row) => {
    const valueEl = row.querySelector('.align-value');
    const raw = valueEl ? valueEl.textContent : row.textContent;
    const valuePart = String(raw || '').replace(/\s+/g, ' ').trim();
    if (!hasDisplayValue(valuePart)) row.remove();
  });

  root.querySelectorAll('.exp-field-table').forEach((table) => {
    const valueEl = table.querySelector('.exp-field-value');
    const raw = valueEl ? valueEl.textContent : table.textContent;
    const valuePart = String(raw || '').replace(/\s+/g, ' ').trim();
    if (!hasDisplayValue(valuePart)) table.remove();
  });

  root.querySelectorAll('.desc-text-block').forEach((el) => {
    if (!hasDisplayValue(el.textContent)) {
      const label = el.previousElementSibling;
      if (label?.classList?.contains('desc-label')) label.remove();
      el.remove();
    }
  });

  root.querySelectorAll('.resp-bullets').forEach((ul) => {
    Array.from(ul.querySelectorAll('li')).forEach((li) => {
      if (!hasDisplayValue(li.textContent)) li.remove();
    });
    if (!ul.querySelector('li')) {
      const label = ul.previousElementSibling;
      if (label?.classList?.contains('resp-label')) label.remove();
      ul.remove();
    }
  });

  root.querySelectorAll('.bullet-list li, .certification-item, .education-item').forEach((li) => {
    if (!hasDisplayValue(li.textContent)) li.remove();
  });

  root.querySelectorAll('.experience-item').forEach((item) => {
    const hasContent =
      item.querySelector(
        '.align-row, .exp-field-list li, .exp-field-table, .desc-text-block, .resp-bullets li, .bullet-list li'
      ) != null;
    if (!hasContent && !(item.textContent || '').trim()) item.remove();
  });
}

/**
 * Work/Project Summary: same editable structure as Technical Skills —
 * ul.exp-field-list > li > .align-row (span label/colon/value). No bullets.
 */
function fixExperienceSummaryFieldLayout(root) {
  if (!root) return;

  const flattenValueEl = (el) => {
    if (!el) return '';
    // Do NOT strip <br> / newlines — Enter must keep working like Technical Skills.
    return String(el.textContent || '').replace(/\u00a0/g, ' ');
  };

  const ensureAlignRowInLi = (li) => {
    if (!li) return;
    let row = li.querySelector('.align-row');
    if (row) {
      const label = row.querySelector('.align-label-exp, .align-label-skills');
      let value = row.querySelector('.align-value');
      let colon = row.querySelector('.align-colon');
      const labelText = String(label?.textContent || '').replace(/\s+/g, ' ').trim();
      const valueText = String(value?.textContent || '').replace(/\s+/g, ' ').trim();
      // Empty Label:Value shells (from Enter) → plain blank line like Technical Skills
      if (!labelText && !valueText) {
        li.innerHTML = '<br>';
        return;
      }
      if (!colon) {
        colon = document.createElement('span');
        colon.className = 'align-colon';
        colon.textContent = ':';
        if (label && value) row.insertBefore(colon, value);
        else row.appendChild(colon);
      } else if (!String(colon.textContent || '').trim() || String(colon.textContent).includes('|')) {
        colon.textContent = ':';
      }
      if (!value) {
        value = document.createElement('span');
        value.className = 'align-value';
        row.appendChild(value);
      } else if (value.tagName !== 'SPAN') {
        const span = document.createElement('span');
        span.className = 'align-value';
        span.textContent = flattenValueEl(value);
        value.replaceWith(span);
        value = span;
      }
      return;
    }

    const raw = (li.textContent || '').replace(/\s+/g, ' ').trim();
    if (!raw) return;
    // Only promote to tabular Label:Value when the line actually has a colon
    const idx = raw.indexOf(':');
    if (idx < 0) return;
    const labelText = raw.slice(0, idx).trim();
    const valueText = raw.slice(idx + 1).trim();
    if (!labelText && !valueText) return;

    row = document.createElement('div');
    row.className = 'align-row';
    const label = document.createElement('span');
    label.className = 'align-label-exp';
    label.textContent = labelText;
    const colon = document.createElement('span');
    colon.className = 'align-colon';
    colon.textContent = ':';
    const value = document.createElement('span');
    value.className = 'align-value';
    value.textContent = valueText;
    row.appendChild(label);
    row.appendChild(colon);
    row.appendChild(value);
    li.innerHTML = '';
    li.appendChild(row);
  };

  root.querySelectorAll('.experience-item .exp-field-table').forEach((table) => {
    const labelText = String(table.querySelector('.exp-field-label')?.textContent || '')
      .replace(/\s+/g, ' ')
      .trim();
    const valueText = flattenValueEl(table.querySelector('.exp-field-value'));
    if (!labelText && !valueText) {
      table.remove();
      return;
    }
    const li = document.createElement('li');
    const row = document.createElement('div');
    row.className = 'align-row';
    const label = document.createElement('span');
    label.className = 'align-label-exp';
    label.textContent = labelText;
    const colon = document.createElement('span');
    colon.className = 'align-colon';
    colon.textContent = ':';
    const value = document.createElement('span');
    value.className = 'align-value';
    value.textContent = valueText;
    row.appendChild(label);
    row.appendChild(colon);
    row.appendChild(value);
    li.appendChild(row);
    table.replaceWith(li);
  });

  root.querySelectorAll('.experience-item').forEach((item) => {
    const directRows = Array.from(item.children).filter(
      (el) => el.classList?.contains('align-row')
    );
    if (directRows.length > 0) {
      let list = item.querySelector('ul.exp-field-list');
      if (!list) {
        list = document.createElement('ul');
        list.className = 'exp-field-list';
        directRows[0].parentNode.insertBefore(list, directRows[0]);
      }
      directRows.forEach((row) => {
        const li = document.createElement('li');
        li.appendChild(row);
        list.appendChild(li);
      });
    }

    item.querySelectorAll('ul.exp-field-list').forEach((list) => {
      Array.from(list.childNodes || []).forEach((node) => {
        if (!node) return;
        if (node.nodeType === 3) {
          if (!String(node.textContent || '').trim()) node.remove();
          return;
        }
        if (node.nodeType !== 1) return;
        if (node.tagName === 'LI') return;
        const li = document.createElement('li');
        li.appendChild(node);
        list.appendChild(li);
      });
      Array.from(list.querySelectorAll('li')).forEach((li) => ensureAlignRowInLi(li));
    });
  });
}

function ensureExperienceAlignGridStyles(doc) {
  if (!doc?.head) return;
  let styleEl = doc.getElementById('experience-align-grid-fix');
  if (!styleEl) {
    styleEl = doc.createElement('style');
    styleEl.id = 'experience-align-grid-fix';
    doc.head.appendChild(styleEl);
  }
  // Same flex pattern as Technical Skills (no grid).
  styleEl.textContent = [
    '.exp-field-list{list-style:none!important;padding-left:0!important;margin-left:0!important;margin-top:2px!important;margin-bottom:4px!important;}',
    '.exp-field-list li{margin-bottom:4px!important;line-height:1.55!important;font-size:11px!important;padding-left:0!important;position:relative!important;list-style:none!important;}',
    '.exp-field-list li::before{content:none!important;display:none!important;}',
    '.experience-item .align-row{display:flex!important;align-items:flex-start!important;width:100%!important;box-sizing:border-box!important;}',
    '.experience-item .align-label-exp{min-width:120px!important;font-weight:bold!important;flex-shrink:0!important;}',
    '.experience-item .align-colon{font-weight:bold!important;margin-left:4px!important;margin-right:8px!important;flex-shrink:0!important;}',
    '.experience-item .align-value{flex:1!important;word-break:break-word!important;}',
  ].join('');
}

function canonicalizeTechnicalSkillsSectionInPlace(root) {
  if (!root) return;
  const norm = (s) => String(s || '').replace(/\s+/g, ' ').trim().toLowerCase().replace(/:$/, '');
  const sections = Array.from(root.querySelectorAll('.section'));
  const skillsSections = sections.filter((sec) => {
    const t = sec.querySelector('.section-title');
    return t && ['technical skill', 'technical skills', 'skills', 'technical skill(s)'].includes(norm(t.textContent));
  });
  if (skillsSections.length === 0) return;

  const ensureAlignRow = (li) => {
    if (!li) return;
    // If already has .align-row, just ensure colon/value spans exist.
    const existingRow = li.querySelector('.align-row');
    if (existingRow) {
      const label = existingRow.querySelector('.align-label-skills, .align-label-exp');
      const value = existingRow.querySelector('.align-value');
      let colon = existingRow.querySelector('.align-colon');
      if (!colon) {
        colon = document.createElement('span');
        colon.className = 'align-colon';
        colon.textContent = ':';
        if (label && value) existingRow.insertBefore(colon, value);
        else existingRow.appendChild(colon);
      } else if (!String(colon.textContent || '').trim()) {
        colon.textContent = ':';
      }
      return;
    }

    // Otherwise, try to parse plain text like "Label: Value" and convert.
    const raw = (li.textContent || '').replace(/\s+/g, ' ').trim();
    if (!raw) return;
    const idx = raw.indexOf(':');
    const labelText = (idx >= 0 ? raw.slice(0, idx) : raw).trim();
    const valueText = (idx >= 0 ? raw.slice(idx + 1) : '').trim();

    // Preserve any rich HTML the user typed by using innerHTML for value when possible.
    // If we can't confidently split by DOM, fall back to plain text.
    const row = document.createElement('div');
    row.className = 'align-row';
    const label = document.createElement('span');
    label.className = 'align-label-skills';
    label.textContent = labelText || raw;
    const colon = document.createElement('span');
    colon.className = 'align-colon';
    colon.textContent = ':';
    const value = document.createElement('span');
    value.className = 'align-value';
    value.textContent = valueText;
    row.appendChild(label);
    row.appendChild(colon);
    row.appendChild(value);
    li.innerHTML = '';
    li.appendChild(row);
  };

  skillsSections.forEach((sec) => {
    const list = sec.querySelector('ul.bullet-list');
    if (!list) return;

    // ContentEditable can insert <div>/<p> directly inside <ul> (invalid HTML),
    // or can create <li> nodes without the expected .align-row structure.
    // Normalize to <li> children first, then enforce align-row + colon.
    Array.from(list.childNodes || []).forEach((node) => {
      if (!node) return;
      if (node.nodeType === 3) {
        // Text node (usually whitespace) — drop it.
        if (!String(node.textContent || '').trim()) node.remove();
        return;
      }
      if (node.nodeType !== 1) return;
      const el = node;
      if (el.tagName === 'LI') return;
      // Wrap any non-LI element into an LI so bullets behave consistently.
      const li = document.createElement('li');
      // Preserve the user's HTML inside this line (it will get normalized below).
      li.innerHTML = el.outerHTML;
      el.replaceWith(li);
    });

    Array.from(list.querySelectorAll('li')).forEach((li) => ensureAlignRow(li));
  });
}

function hideEmptyResumeFields(htmlString) {
  if (!htmlString || typeof htmlString !== 'string') return htmlString;
  const trimmed = htmlString.trim();
  const isFull = isFullHtmlDocument(trimmed);
  const doc = parseHtmlDocument(
    isFull ? trimmed : `<!doctype html><html><head></head><body>${htmlString}</body></html>`
  );
  if (!doc?.body) return htmlString;
  const root = doc.body.querySelector('.resume-container') || doc.body;
  removeEmptyResumeFieldRows(root);
  if (isFull) return `<!doctype html>\n${doc.documentElement.outerHTML}`;
  return root.outerHTML || doc.body.innerHTML;
}

function normalizeSectionTitleText(text) {
  return String(text || '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/:$/, '')
    .toLowerCase();
}

/** Only dedupe repeated Feuji section headings (`.section-title`), not body text like "Skills:" in projects. */
function trimAtRepeatedSectionTitles(root) {
  if (!root) return false;
  const removeFromNodeOnwardWithinRoot = (node) => {
    if (!node) return false;
    let cut = node;
    while (cut && cut.parentNode && cut.parentNode !== root) {
      cut = cut.parentNode;
    }
    if (!cut || cut.parentNode !== root) return false;
    const parent = cut.parentNode;
    let cur = cut;
    while (cur) {
      const next = cur.nextSibling;
      parent.removeChild(cur);
      cur = next;
    }
    return true;
  };

  const seen = new Set();
  const titles = Array.from(root.querySelectorAll('.section-title'));
  for (const el of titles) {
    const key = normalizeSectionTitleText(el.textContent);
    if (!key) continue;
    if (seen.has(key)) {
      removeFromNodeOnwardWithinRoot(el);
      return true;
    }
    seen.add(key);
  }
  return false;
}

function hardTrimToSingleResumeFragment(htmlString) {
  // Deterministic de-dupe: keep ONLY the first resume block and remove any repeated copy,
  // even if classes/styles were altered by contentEditable.
  const doc = parseHtmlDocument(`<!doctype html><html><head></head><body>${htmlString || ''}</body></html>`);
  if (!doc || !doc.body) return htmlString;

  const root = doc.body.querySelector('.resume-container') || doc.body;

  const removeFromNodeOnwardWithinRoot = (node) => {
    if (!node) return false;
    let cut = node;
    while (cut && cut.parentNode && cut.parentNode !== root && cut.parentNode !== doc.body) {
      cut = cut.parentNode;
    }
    if (!cut || !cut.parentNode) return false;
    const parent = cut.parentNode;
    let cur = cut;
    while (cur) {
      const next = cur.nextSibling;
      parent.removeChild(cur);
      cur = next;
    }
    return true;
  };

  // 1) Multiple resume containers => keep first only.
  const containers = Array.from(doc.body.querySelectorAll('.resume-container'));
  if (containers.length > 1) {
    for (let i = 1; i < containers.length; i++) containers[i].remove();
    return containers[0].outerHTML;
  }

  // 2) Repeated Feuji section headings only (not "Skills:" / "Experience" in body copy).
  if (trimAtRepeatedSectionTitles(root)) {
    return (doc.body.querySelector('.resume-container') || root).outerHTML || doc.body.innerHTML;
  }

  // 3) If there are multiple Feuji header logo images, cut at the second.
  const logoImgs = Array.from(root.querySelectorAll('img')).filter((img) =>
    String(img.getAttribute('src') || '').includes('feuji-logo-header')
  );
  if (logoImgs.length > 1 && removeFromNodeOnwardWithinRoot(logoImgs[1])) {
    return (doc.body.querySelector('.resume-container') || root).outerHTML || doc.body.innerHTML;
  }

  return (doc.body.querySelector('.resume-container') || root).outerHTML || doc.body.innerHTML;
}

function getHeadStylesText(htmlString) {
  if (!isFullHtmlDocument(htmlString)) return '';
  const doc = parseHtmlDocument(htmlString);
  if (!doc) return '';
  return Array.from(doc.querySelectorAll('head style'))
    .map((el) => el.textContent || '')
    .join('\n')
    .trim();
}

function getBodyHtmlForEditing(htmlString) {
  if (!isFullHtmlDocument(htmlString)) return htmlString;
  const doc = parseHtmlDocument(htmlString);
  if (!doc) return htmlString;
  // Keep exactly what the preview renders (header + content), but without <html>/<head> wrappers.
  return doc.body ? doc.body.innerHTML : htmlString;
}

function upsertUserEditStyle(doc, cssText) {
  if (!doc || !doc.head) return;
  const STYLE_ID = 'resume-formatter-user-edit-style';
  let el = doc.getElementById(STYLE_ID);
  if (!el) {
    el = doc.createElement('style');
    el.id = STYLE_ID;
    doc.head.appendChild(el);
  }
  el.textContent = cssText || '';
}

function toBodyInnerHtml(htmlString) {
  // If someone pasted a full document into the editor, normalize to body HTML only.
  if (!htmlString || typeof htmlString !== 'string') return htmlString;
  const doc = parseHtmlDocument(htmlString);
  if (!doc) return htmlString;
  return doc.body ? doc.body.innerHTML : htmlString;
}

/** Normalize CSS color to #rrggbb for color inputs. */
function cssColorToHex(cssColor) {
  if (!cssColor || typeof cssColor !== 'string') return null;
  const t = cssColor.trim();
  if (/^#[0-9a-f]{3}$/i.test(t)) {
    const r = t[1];
    const g = t[2];
    const b = t[3];
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  if (/^#[0-9a-f]{6}$/i.test(t)) return t.toLowerCase();
  const m = t.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (!m) return null;
  const hex = (n) => Math.min(255, Math.max(0, Number(n))).toString(16).padStart(2, '0');
  return `#${hex(m[1])}${hex(m[2])}${hex(m[3])}`;
}

function hexToRgb(hex) {
  const normalized = cssColorToHex(hex) || '#ffffff';
  const m = normalized.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
  if (!m) return { r: 255, g: 255, b: 255 };
  return {
    r: parseInt(m[1], 16),
    g: parseInt(m[2], 16),
    b: parseInt(m[3], 16),
  };
}

/** Read resume page background from .resume-container (inline style from editor). */
function getResumePageBackgroundColor(rootEl) {
  if (!rootEl) return '#ffffff';
  const container = rootEl.querySelector?.('.resume-container') || rootEl;
  const inline = container.style?.backgroundColor;
  if (inline) {
    const hex = cssColorToHex(inline);
    if (hex) return hex;
  }
  if (typeof window !== 'undefined' && window.getComputedStyle) {
    const computed = window.getComputedStyle(container).backgroundColor;
    const hex = cssColorToHex(computed);
    if (hex) return hex;
  }
  return '#ffffff';
}

function applyResumePageBackgroundToPrintRoot(rootEl, color) {
  if (!rootEl || !color) return;
  rootEl.style.backgroundColor = color;
  const section = rootEl.querySelector('.WordSection1');
  const container = rootEl.querySelector('.resume-container');
  if (section) section.style.backgroundColor = color;
  if (container) container.style.backgroundColor = color;
}

/** Toolbar CSS for saving into the final HTML document (targets body, correct for full-doc context). */
function buildUserEditCss(fontFamily, fontSize) {
  return `
      body {
        font-family: ${fontFamily};
        font-size: ${fontSize};
      }
    `;
}

/** Toolbar CSS scoped to the editor div — prevents font changes leaking to the main app layout. */
function buildEditorScopedCss(fontFamily, fontSize) {
  return `
      .resume-formatter-editable {
        font-family: ${fontFamily};
        font-size: ${fontSize};
      }
      .resume-formatter-editable ul.bullet-list,
      .resume-formatter-editable ul.resp-bullets,
      .resume-formatter-editable ul {
        list-style: none;
        padding-left: 0;
        margin-left: 20px;
      }
      .resume-formatter-editable .bullet-list li,
      .resume-formatter-editable .resp-bullets li {
        position: relative;
        padding-left: 18px;
      }
      .resume-formatter-editable .bullet-list li::before,
      .resume-formatter-editable .resp-bullets li::before {
        content: '\\2022';
        position: absolute;
        left: 0;
        top: 0.1em;
        font-size: 14px;
        line-height: 1;
        color: #000;
      }
      /* Work/Project Summary — same flex align-row as Technical Skills (inside exp-field-list). */
      .resume-formatter-editable .exp-field-list {
        list-style: none !important;
        padding-left: 0 !important;
        margin-left: 0 !important;
      }
      .resume-formatter-editable .exp-field-list li {
        position: relative !important;
        padding-left: 0 !important;
        list-style: none !important;
      }
      .resume-formatter-editable .exp-field-list li::before {
        content: none !important;
        display: none !important;
      }
      .resume-formatter-editable .experience-item .align-row {
        display: flex !important;
        align-items: flex-start !important;
        width: 100% !important;
        box-sizing: border-box !important;
      }
      .resume-formatter-editable .experience-item .align-label-exp {
        min-width: 120px !important;
        font-weight: bold !important;
        flex-shrink: 0 !important;
      }
      .resume-formatter-editable .experience-item .align-colon {
        flex-shrink: 0 !important;
        font-weight: bold !important;
        margin-left: 4px !important;
        margin-right: 8px !important;
      }
      .resume-formatter-editable .experience-item .align-value {
        flex: 1 !important;
        word-break: break-word !important;
      }
    `;
}

/** Strip ALL html/body rules from style text before injecting into a contentEditable div
 *  so resume CSS doesn't affect the host page layout. */
function stripAllBodyHtmlStyles(cssText) {
  if (!cssText || typeof cssText !== 'string') return '';
  return cssText.replace(/\b(html|body)\s*\{[^}]*\}/gi, '');
}

/** Remove global body color rules (legacy saves / edit injection) so they do not cascade to the whole resume. */
function stripGlobalBodyColorFromStyles(cssText) {
  if (!cssText || typeof cssText !== 'string') return '';
  return cssText.replace(/\bbody\s*\{([^}]*)\}/gi, (match, inner) => {
    const cleaned = inner
      .replace(/\bcolor\s*:\s*[^;]+;?/gi, '')
      .replace(/\bbackground-color\s*:\s*[^;]+;?/gi, '');
    return `body {${cleaned}}`;
  });
}

/** True when a text node has any overlap with the range. */
function textNodeIntersectsRange(textNode, range) {
  if (!textNode || textNode.nodeType !== Node.TEXT_NODE) return false;
  try {
    if (typeof range.intersectsNode === 'function') {
      return range.intersectsNode(textNode);
    }
  } catch {
    // intersectsNode throws if textNode is not in the same document tree as range
  }
  const nodeRange = document.createRange();
  nodeRange.selectNodeContents(textNode);
  return (
    range.compareBoundaryPoints(Range.END_TO_START, nodeRange) < 0 &&
    range.compareBoundaryPoints(Range.START_TO_END, nodeRange) > 0
  );
}

/** Collect text nodes touched by a range (does not mutate the DOM). */
function getTextNodesInRange(range) {
  const root = range.commonAncestorContainer;
  const walkRoot =
    root.nodeType === Node.TEXT_NODE ? root.parentNode : root;
  if (!walkRoot) return [];

  const nodes = [];
  const walker = document.createTreeWalker(walkRoot, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      if (!node.textContent?.length) return NodeFilter.FILTER_REJECT;
      return textNodeIntersectsRange(node, range)
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT;
    },
  });
  while (walker.nextNode()) {
    nodes.push(walker.currentNode);
  }
  return nodes;
}

/** Character offset of a range boundary within a specific text node. */
function boundaryOffsetInTextNode(textNode, container, offset) {
  const nodeRange = document.createRange();
  nodeRange.selectNodeContents(textNode);

  const boundary = document.createRange();
  boundary.setStart(container, offset);
  boundary.collapse(true);

  if (boundary.compareBoundaryPoints(Range.START_TO_START, nodeRange) <= 0) {
    return 0;
  }

  const nodeEnd = document.createRange();
  nodeEnd.selectNodeContents(textNode);
  nodeEnd.collapse(false);

  if (boundary.compareBoundaryPoints(Range.START_TO_START, nodeEnd) >= 0) {
    return textNode.length;
  }

  const measure = document.createRange();
  measure.setStart(nodeRange.startContainer, nodeRange.startOffset);
  measure.setEnd(container, offset);
  return measure.toString().length;
}

/**
 * Apply inline CSS to a selection by wrapping only text-node segments.
 * Avoids extractContents/surroundContents, which split <li>/<ul> and create extra bullets.
 */
function applyInlineStyleToRange(range, styleProp, value) {
  if (!range || range.collapsed) return;

  const textNodes = getTextNodesInRange(range);
  for (const textNode of textNodes) {
    const start = boundaryOffsetInTextNode(
      textNode,
      range.startContainer,
      range.startOffset
    );
    const end = boundaryOffsetInTextNode(
      textNode,
      range.endContainer,
      range.endOffset
    );
    if (start >= end) continue;

    let node = textNode;
    if (end < node.length) {
      node.splitText(end);
    }
    if (start > 0) {
      node = node.splitText(start);
    }

    const span = document.createElement('span');
    span.style[styleProp] = value;
    const parent = node.parentNode;
    if (!parent) continue;
    parent.insertBefore(span, node);
    span.appendChild(node);
  }
}

/** PDF export: html2canvas mis-renders native list-style discs; use aligned pseudo-bullets instead. */
function injectPdfExportStyles(container) {
  if (!container) return;
  const style = document.createElement('style');
  style.id = 'resume-pdf-export-fixes';
  style.textContent = `
    .bullet-list,
    .resp-bullets {
      list-style: none !important;
      padding-left: 0 !important;
      margin-left: 20px !important;
    }
    .resp-bullets { margin-left: 15px !important; }
    .bullet-list li,
    .resp-bullets li {
      position: relative !important;
      padding-left: 18px !important;
      margin-bottom: 4px !important;
      line-height: 1.55 !important;
      list-style: none !important;
    }
    .bullet-list li::before,
    .resp-bullets li::before {
      content: '\\2022' !important;
      position: absolute !important;
      left: 0 !important;
      top: 0.1em !important;
      font-size: 14px !important;
      line-height: 1 !important;
      color: #000 !important;
    }
    .resume-content {
      padding-top: 6px !important;
    }
    .name {
      margin-top: 8px !important;
    }
  `;
  container.insertBefore(style, container.firstChild);

  const headerEl = container.querySelector('.header');
  if (headerEl) headerEl.style.display = 'none';

  const resumeContent = container.querySelector('.resume-content');
  if (resumeContent) {
    resumeContent.style.paddingTop = '8px';
  }
}

/** Collect block-level break positions for PDF pagination (canvas scale = 1x DOM pixels). */
function collectPdfBreakpointData(resumeRoot) {
  const resumeTop = resumeRoot.getBoundingClientRect().top;
  const points = [];
  const seen = new Set();

  const addPoint = (y, meta) => {
    const py = Math.floor(y - resumeTop);
    if (!Number.isFinite(py) || py <= 0) return;
    const key = `${py}:${meta.kind}:${meta.edge}`;
    if (seen.has(key)) return;
    seen.add(key);
    points.push({ y: py, ...meta });
  };

  const selectorKinds = [
    ['.name', 'block'],
    ['.section-title', 'heading'],
    ['.subsection-title', 'heading'],
    ['.project-number', 'heading'],
    ['.experience-item', 'block'],
    ['.align-row', 'row'],
    ['.exp-field-table', 'row'],
    ['.desc-text-block', 'block'],
    ['.resp-bullets li', 'bullet'],
    ['.bullet-list li', 'bullet'],
  ];

  selectorKinds.forEach(([selector, kind]) => {
    resumeRoot.querySelectorAll(selector).forEach((el) => {
      const rect = el.getBoundingClientRect();
      addPoint(rect.top, { kind, edge: 'top', el });
      if (kind !== 'heading') {
        addPoint(rect.bottom, { kind, edge: 'bottom', el });
      }
    });
  });

  return points.sort((a, b) => a.y - b.y);
}

/** Prefer breaks close to the page end while avoiding orphan headings and mid-block splits. */
function pickPdfPageEnd(breakpoints, yOffset, idealEnd, totalHeight, scale = 2) {
  const minPagePx = 160 * scale;
  const minContentAfterHeading = 48 * scale;
  const yOff = yOffset;
  const ideal = idealEnd;

  if (ideal >= totalHeight) return totalHeight;

  const scaledPoints = breakpoints.map((bp) => ({
    ...bp,
    y: bp.y * scale,
  }));

  const candidates = scaledPoints.filter(
    (bp) => bp.y <= ideal && bp.y - yOff >= minPagePx && bp.edge === 'top'
  );

  for (let i = candidates.length - 1; i >= 0; i--) {
    const bp = candidates[i];
    if (bp.kind === 'heading' && bp.el) {
      const rect = bp.el.getBoundingClientRect();
      const resumeRoot = bp.el.closest('.resume-container') || bp.el.parentElement;
      const rootTop = resumeRoot ? resumeRoot.getBoundingClientRect().top : 0;
      const headingBottom = (rect.bottom - rootTop) * scale;
      let nextContentBottom = headingBottom;
      let sibling = bp.el.nextElementSibling;
      while (sibling && sibling.classList?.contains('section-title')) {
        sibling = sibling.nextElementSibling;
      }
      if (sibling) {
        const sibRect = sibling.getBoundingClientRect();
        nextContentBottom = (sibRect.bottom - rootTop) * scale;
      }
      const contentOnPage = Math.min(nextContentBottom, ideal) - bp.y;
      if (contentOnPage < minContentAfterHeading) {
        continue;
      }
    }
    return bp.y;
  }

  const anyBreak = scaledPoints
    .filter((bp) => bp.y <= ideal && bp.y - yOff >= minPagePx)
    .sort((a, b) => b.y - a.y)[0];
  return anyBreak ? anyBreak.y : ideal;
}

function getBlockElementForSpacing(node, root) {
  let el = node?.nodeType === Node.TEXT_NODE ? node.parentElement : node;
  while (el && el !== root) {
    const tag = el.tagName;
    if (tag && ['P', 'DIV', 'LI', 'TD', 'H1', 'H2', 'H3', 'H4', 'SPAN'].includes(tag)) {
      if (tag === 'SPAN' && el.parentElement && el.parentElement !== root) {
        const parentTag = el.parentElement.tagName;
        if (['P', 'DIV', 'LI', 'TD'].includes(parentTag)) {
          return el.parentElement;
        }
      }
      if (tag !== 'SPAN') return el;
    }
    el = el.parentElement;
  }
  return null;
}

function collectParagraphBlocksInRange(range, root) {
  const blocks = new Set();
  if (!range || !root) return blocks;

  if (range.collapsed) {
    const block = getBlockElementForSpacing(range.startContainer, root);
    if (block) blocks.add(block);
    return blocks;
  }

  getTextNodesInRange(range).forEach((textNode) => {
    const block = getBlockElementForSpacing(textNode, root);
    if (block) blocks.add(block);
  });

  const startBlock = getBlockElementForSpacing(range.startContainer, root);
  const endBlock = getBlockElementForSpacing(range.endContainer, root);
  if (startBlock) blocks.add(startBlock);
  if (endBlock) blocks.add(endBlock);
  return blocks;
}

function applyLineSpacingToSelection(root, spacing) {
  if (!root || !spacing) return;
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return;
  const range = selection.getRangeAt(0);
  if (!root.contains(range.commonAncestorContainer)) return;

  const blocks = collectParagraphBlocksInRange(range, root);
  if (blocks.size === 0) {
    const fallback = getBlockElementForSpacing(range.startContainer, root);
    if (fallback) blocks.add(fallback);
  }

  blocks.forEach((block) => {
    block.style.lineHeight = String(spacing);
  });
  selection.removeAllRanges();
  root.focus();
}

const LINE_SPACING_OPTIONS = ['1.0', '1.15', '1.5', '2.0', '2.5', '3.0'];

function buildSingleResumeHtmlDocument({ headStylesText, userCssText, bodyHtml }) {
  const styles = [headStylesText, userCssText].filter(Boolean).join('\n');
  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style id="resume-formatter-user-edit-style">${styles}</style>
  </head>
  <body>
${bodyHtml || ''}
    <script>
      (function () {
        function removeDuplicates() {
          try {
            var containers = document.querySelectorAll('.resume-container');
            if (containers && containers.length > 1) {
              for (var i = 1; i < containers.length; i++) containers[i].remove();
            }
            var root = document.querySelector('.resume-container') || document.body;

            function cutFromNode(node) {
              if (!node) return false;
              // Bubble up until we're a direct child of root (or body as fallback).
              var cut = node;
              while (cut && cut.parentNode && cut.parentNode !== root && cut.parentNode !== document.body) {
                cut = cut.parentNode;
              }
              if (!cut || !cut.parentNode) return false;
              var parent = cut.parentNode;
              // Remove cut and all following siblings
              var cur = cut;
              while (cur) {
                var nxt = cur.nextSibling;
                parent.removeChild(cur);
                cur = nxt;
              }
              return true;
            }

            // Cut everything from the 2nd header/logo/name onward (robust across nesting)
            var headers = root.querySelectorAll('.header');
            if (headers && headers.length > 1 && cutFromNode(headers[1])) return;

            var logos = Array.prototype.filter.call(root.querySelectorAll('img'), function (img) {
              return String(img.getAttribute('src') || '').indexOf('feuji-logo-header') !== -1;
            });
            if (logos.length > 1 && cutFromNode(logos[1])) return;

            var names = root.querySelectorAll('.name');
            if (names && names.length > 1 && cutFromNode(names[1])) return;

            // Repeated section titles only (class section-title), not body labels like "Skills:".
            var seenTitles = {};
            var titles = root.querySelectorAll('.section-title');
            for (var t = 0; t < titles.length; t++) {
              var titleEl = titles[t];
              var key = String(titleEl.textContent || '').replace(/\s+/g, ' ').trim().replace(/:$/, '').toLowerCase();
              if (!key) continue;
              if (seenTitles[key]) {
                cutFromNode(titleEl);
                return;
              }
              seenTitles[key] = true;
            }
          } catch (e) {
            // ignore
          }
        }
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', removeDuplicates);
        } else {
          removeDuplicates();
        }
      })();
    </script>
  </body>
</html>`;
}

function injectDedupeScriptIntoFullHtml(htmlString) {
  if (!isFullHtmlDocument(htmlString)) return htmlString;
  const doc = parseHtmlDocument(htmlString);
  if (!doc || !doc.body) return htmlString;
  // If script already present, don't re-add
  if (doc.getElementById('resume-dedupe-script')) {
    return `<!doctype html>\n${doc.documentElement.outerHTML}`;
  }
  const script = doc.createElement('script');
  script.id = 'resume-dedupe-script';
  script.textContent = `
    (function () {
      function removeDuplicates() {
        try {
          var containers = document.querySelectorAll('.resume-container');
          if (containers && containers.length > 1) {
            for (var i = 1; i < containers.length; i++) containers[i].remove();
          }
          var root = document.querySelector('.resume-container') || document.body;

          function cutFromNode(node) {
            if (!node) return false;
            var cut = node;
            while (cut && cut.parentNode && cut.parentNode !== root && cut.parentNode !== document.body) {
              cut = cut.parentNode;
            }
            if (!cut || !cut.parentNode) return false;
            var parent = cut.parentNode;
            var cur = cut;
            while (cur) { var nxt = cur.nextSibling; parent.removeChild(cur); cur = nxt; }
            return true;
          }

          var headers = root.querySelectorAll('.header');
          if (headers && headers.length > 1 && cutFromNode(headers[1])) return;
          var logos = Array.prototype.filter.call(root.querySelectorAll('img'), function (img) {
            return String(img.getAttribute('src') || '').indexOf('feuji-logo-header') !== -1;
          });
          if (logos.length > 1 && cutFromNode(logos[1])) return;
          var names = root.querySelectorAll('.name');
          if (names && names.length > 1 && cutFromNode(names[1])) return;

          // Content-based fallback: if resume section headings appear twice, cut from the second.
          var headingRes = [
            /^professional summary\b/i,
            /^technical skills\b/i,
            /^professional experience\b/i,
            /^education\b/i,
            /^summary\b/i,
            /^skills\b/i,
            /^experience\b/i
          ];
          var all = Array.prototype.slice.call(root.querySelectorAll('*'));
          function normText(s) { return String(s || '').replace(/\s+/g, ' ').trim(); }
          var shortEls = all
            .map(function (el) { return { el: el, text: normText(el.textContent) }; })
            .filter(function (x) { return x.text && x.text.length <= 60; });
          for (var r = 0; r < headingRes.length; r++) {
            var re = headingRes[r];
            var hits = shortEls.filter(function (x) { return re.test(x.text); });
            if (hits.length > 1 && cutFromNode(hits[1].el)) return;
          }
        } catch (e) {}
      }
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', removeDuplicates);
      else removeDuplicates();
    })();
  `;
  doc.body.appendChild(script);
  return `<!doctype html>\n${doc.documentElement.outerHTML}`;
}

function sanitizeResumeHtml(htmlString) {
  if (!htmlString || typeof htmlString !== 'string') return htmlString;
  const trimmed = htmlString.trim();

  // Sanitize fragments too (not only full docs).
  if (!isFullHtmlDocument(trimmed)) {
    const deduped = dedupeResumeBodyHtml(trimmed);
    const primary = extractPrimaryResumeFragment(deduped);
    const fragDoc = parseHtmlDocument(
      `<!doctype html><html><head></head><body>${hardTrimToSingleResumeFragment(primary)}</body></html>`
    );
    if (fragDoc?.body) {
      const root = fragDoc.body.querySelector('.resume-container') || fragDoc.body;
      fixExperienceSummaryFieldLayout(root);
      removeEmptyResumeFieldRows(root);
      return root.outerHTML || fragDoc.body.innerHTML;
    }
    return hideEmptyResumeFields(hardTrimToSingleResumeFragment(primary));
  }

  const doc = parseHtmlDocument(trimmed);
  if (!doc || !doc.body) return htmlString;

  // 1) Dedupe within body (handles "second copy" inside same container)
  const dedupedBody = dedupeResumeBodyHtml(doc.body.innerHTML);
  // 2) Keep only the first resume container as the body (prevents multiple blocks)
  const primary = extractPrimaryResumeFragment(dedupedBody);
  doc.body.innerHTML = hardTrimToSingleResumeFragment(primary || '');
  const root = doc.body.querySelector('.resume-container') || doc.body;
  fixExperienceSummaryFieldLayout(root);
  removeEmptyResumeFieldRows(root);
  ensureExperienceAlignGridStyles(doc);

  // Do not inject iframe dedupe script on initial format — it was stripping valid content
  // (e.g. resumes with multiple "Skills:" labels inside project sections).
  return `<!doctype html>\n${doc.documentElement.outerHTML}`;
}

/** Preview-only: remove extra `.resume-container` copies. Avoid hardTrim/dedupe string heuristics here —
 * they run on initial format (`sanitizeResumeHtml`) and on save; re-running them on every preview
 * blanked the iframe after Save Changes. */
function lightPreviewDedupeResumeContainers(bodyEl) {
  if (!bodyEl) return;
  const resumeContainers = bodyEl.querySelectorAll('.resume-container');
  if (resumeContainers.length <= 1) return;
  const firstNonEmpty =
    Array.from(resumeContainers).find((node) => (node.textContent || '').trim().length > 0) ||
    resumeContainers[0];
  Array.from(resumeContainers).forEach((node) => {
    if (node !== firstNonEmpty) node.remove();
  });
}

function stripBodyColorsFromDocumentStyles(doc) {
  if (!doc || !doc.head) return;
  doc.querySelectorAll('head style').forEach((el) => {
    if (el.textContent) {
      el.textContent = stripGlobalBodyColorFromStyles(el.textContent);
    }
  });
}

function buildPreviewOnlyHtml(formattedHtml) {
  if (!formattedHtml || typeof formattedHtml !== 'string') return '';
  const trimmed = formattedHtml.trim();

  if (!isFullHtmlDocument(trimmed)) {
    const doc = parseHtmlDocument(`<!doctype html><html><head></head><body>${trimmed}</body></html>`);
    if (!doc || !doc.body) return trimmed;
    doc.querySelectorAll('style').forEach((el) => {
      if (el.textContent) el.textContent = stripGlobalBodyColorFromStyles(el.textContent);
    });
    lightPreviewDedupeResumeContainers(doc.body);
    const root = doc.body.querySelector('.resume-container') || doc.body;
    fixExperienceSummaryFieldLayout(root);
    ensureExperienceAlignGridStyles(doc);
    return doc.body.innerHTML;
  }

  const doc = parseHtmlDocument(trimmed);
  if (!doc || !doc.body) return trimmed;
  stripBodyColorsFromDocumentStyles(doc);
  lightPreviewDedupeResumeContainers(doc.body);
  const root = doc.body.querySelector('.resume-container') || doc.body;
  fixExperienceSummaryFieldLayout(root);
  ensureExperienceAlignGridStyles(doc);
  return `<!doctype html>\n${doc.documentElement.outerHTML}`;
}

function extractPrimaryResumeFragment(bodyHtml) {
  // Normalize saved content to a single resume block so edits replace, not append.
  const doc = parseHtmlDocument(`<!doctype html><html><head></head><body>${bodyHtml || ''}</body></html>`);
  if (!doc || !doc.body) return bodyHtml;
  const first = doc.body.querySelector('.resume-container');
  if (first) {
    // If the edited HTML accidentally contains nested/duplicate resume containers,
    // keep only the first one (the current `first`) and remove the rest.
    const nestedContainers = Array.from(first.querySelectorAll('.resume-container'));
    if (nestedContainers.length > 1) {
      for (let i = 1; i < nestedContainers.length; i++) nestedContainers[i].remove();
    }

    // Remove nested duplicate resume content inside the container (2nd header/logo/name).
    const headers = Array.from(first.querySelectorAll('.header'));
    if (headers.length > 1) {
      const second = headers[1];
      // remove everything after (and including) the second header within the container
      let node = second;
      while (node) {
        const next = node.nextSibling;
        node.remove();
        node = next;
      }
    } else {
      const logos = Array.from(first.querySelectorAll('img')).filter((img) =>
        String(img.getAttribute('src') || '').includes('feuji-logo-header')
      );
      if (logos.length > 1) {
        let node = logos[1];
        while (node) {
          const next = node.nextSibling;
          node.remove();
          node = next;
        }
      } else {
        const names = Array.from(first.querySelectorAll('.name'));
        if (names.length > 1) {
          let node = names[1];
          while (node) {
            const next = node.nextSibling;
            node.remove();
            node = next;
          }
        }
      }
    }
    return first.outerHTML;
  }
  // Fallback: if there is a single top-level element, keep that.
  const els = Array.from(doc.body.children || []);
  if (els.length === 1) return els[0].outerHTML;
  return doc.body.innerHTML;
}

function extractSingleResumeFromEditedHtml(htmlString) {
  // Strong guarantee for "Save Changes": keep ONLY the first resume block and drop
  // anything after it (prevents "appended to next page" duplicates).
  const doc = parseHtmlDocument(`<!doctype html><html><head></head><body>${htmlString || ''}</body></html>`);
  if (!doc || !doc.body) return htmlString;

  const firstContainer = doc.body.querySelector('.resume-container');
  if (firstContainer) {
    // Remove everything after the first resume container at the top level
    // (handles the common "two copies back-to-back" case).
    let cur = firstContainer.nextSibling;
    while (cur) {
      const next = cur.nextSibling;
      cur.remove();
      cur = next;
    }

    // Also remove any additional resume containers anywhere.
    const all = Array.from(doc.body.querySelectorAll('.resume-container'));
    for (let i = 1; i < all.length; i++) all[i].remove();

    // Finally, remove "second copy" content *within* the remaining container, if present.
    // This is the common case where the editor ends up appending a duplicate block inside the same container.
    const removeFromNodeOnwardWithinRoot = (root, node) => {
      if (!root || !node) return false;
      let cut = node;
      while (cut && cut.parentNode && cut.parentNode !== root) {
        cut = cut.parentNode;
      }
      if (!cut || cut.parentNode !== root) return false;
      let cur = cut;
      while (cur) {
        const next = cur.nextSibling;
        root.removeChild(cur);
        cur = next;
      }
      return true;
    };

    const headers = Array.from(firstContainer.querySelectorAll('.header'));
    if (headers.length > 1 && removeFromNodeOnwardWithinRoot(firstContainer, headers[1])) {
      return firstContainer.outerHTML;
    }

    const logoImgs = Array.from(firstContainer.querySelectorAll('img')).filter((img) =>
      String(img.getAttribute('src') || '').includes('feuji-logo-header')
    );
    if (logoImgs.length > 1 && removeFromNodeOnwardWithinRoot(firstContainer, logoImgs[1])) {
      return firstContainer.outerHTML;
    }

    const names = Array.from(firstContainer.querySelectorAll('.name'));
    if (names.length > 1) {
      removeFromNodeOnwardWithinRoot(firstContainer, names[1]);
    }

    return hardTrimToSingleResumeFragment(firstContainer.outerHTML);
  }

  // If the formatter HTML is missing `.resume-container`, fall back to existing logic.
  return hardTrimToSingleResumeFragment(extractPrimaryResumeFragment(doc.body.innerHTML));
}

function dedupeResumeBodyHtml(bodyHtml) {
  // Defensive: if the editor ends up with multiple copies of the resume (user edits one,
  // preview shows another), keep only the first resume block.
  const doc = parseHtmlDocument(`<!doctype html><html><head></head><body>${bodyHtml || ''}</body></html>`);
  if (!doc || !doc.body) return bodyHtml;

  const containers = Array.from(doc.body.querySelectorAll('.resume-container'));
  // Keep only the first resume container if multiple exist.
  if (containers.length > 1) {
    for (let i = 1; i < containers.length; i++) containers[i].remove();
  }

  const root = containers[0] || doc.body;

  const removeFromNodeOnwardWithinRoot = (node) => {
    if (!node) return;
    // Find the direct child of root that contains `node`
    let cut = node;
    while (cut && cut.parentNode && cut.parentNode !== root) {
      cut = cut.parentNode;
    }
    if (!cut || cut.parentNode !== root) return;

    let cur = cut;
    while (cur) {
      const next = cur.nextSibling;
      root.removeChild(cur);
      cur = next;
    }
  };

  // 1) Prefer cutting at the second explicit header (ideal when classes are preserved)
  const headers = Array.from(root.querySelectorAll('.header'));
  if (headers.length > 1) {
    removeFromNodeOnwardWithinRoot(headers[1]);
    return doc.body.innerHTML;
  }

  // 2) If editing stripped classes, detect repeated Feuji header logo occurrences.
  const logoImgs = Array.from(root.querySelectorAll('img'))
    .filter((img) => String(img.getAttribute('src') || '').includes('feuji-logo-header'));
  if (logoImgs.length > 1) {
    removeFromNodeOnwardWithinRoot(logoImgs[1]);
    return doc.body.innerHTML;
  }

  // 3) If classes are stripped, detect repeated orange header bars and cut from the second onward.
  const orangeBars = Array.from(root.querySelectorAll('[style]')).filter((el) => {
    const style = String(el.getAttribute('style') || '').toLowerCase();
    return (
      style.includes('#f3762d') ||
      style.includes('rgb(243, 118, 45)') ||
      (style.includes('background') && style.includes('f3762d'))
    );
  });
  if (orangeBars.length > 1) {
    removeFromNodeOnwardWithinRoot(orangeBars[1]);
    return doc.body.innerHTML;
  }

  // 4) Repeated Feuji section headings (`.section-title` only).
  if (trimAtRepeatedSectionTitles(root)) {
    return doc.body.innerHTML;
  }

  // 5) Fallback: if the resume name block appears twice, cut from the second one onward.
  const names = Array.from(root.querySelectorAll('.name'));
  if (names.length > 1) {
    removeFromNodeOnwardWithinRoot(names[1]);
    return doc.body.innerHTML;
  }

  return doc.body.innerHTML;
}

const PASSWORD_PROTECTED_PDF_MESSAGE =
  'This PDF file is password protected.\nPassword-protected PDF files are not supported.';

const INVALID_OR_CORRUPTED_PDF_MESSAGE =
  'The uploaded PDF file is invalid or corrupted. Please upload a valid PDF file.';

/** Unwrap Java `detail` when it contains a JSON FastAPI body: {"detail":"..."} */
function extractFormatterErrorMessage(error) {
  const data = error?.response?.data;
  if (!data || typeof data !== 'object') {
    return error?.message || '';
  }
  const parseNestedJsonDetail = (raw) => {
    if (typeof raw !== 'string') return null;
    const t = raw.trim();
    if (!t.startsWith('{')) return null;
    try {
      const o = JSON.parse(t);
      if (typeof o.detail === 'string') return o.detail;
      if (Array.isArray(o.detail) && o.detail[0]?.msg) return String(o.detail[0].msg);
    } catch {
      return null;
    }
    return null;
  };
  const nested = parseNestedJsonDetail(data.detail);
  if (nested) return nested;

  const normalizePart = (s) => String(s || '').replace(/\s+/g, ' ').trim().toLowerCase();
  const uniqueParts = [];
  for (const raw of [data.message, data.detail]) {
    if (typeof raw !== 'string') continue;
    const trimmed = raw.trim();
    if (!trimmed) continue;
    const key = normalizePart(trimmed);
    if (!uniqueParts.some((p) => normalizePart(p) === key)) {
      uniqueParts.push(trimmed);
    }
  }
  return uniqueParts.length ? uniqueParts.join(' — ') : error?.message || '';
}

const ResumeFormatter = () => {
  const FILE_CACHE_KEY = 'resume_formatter_file_meta';
  const [formatPath, setFormatPath] = useState('existing');
  const [companyTemplate, setCompanyTemplate] = useState(null);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [formatCount, setFormatCount] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState('feuji1');
  const [previewTemplateId, setPreviewTemplateId] = useState(null);
  const [templateDropdownOpen, setTemplateDropdownOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [batchItems, setBatchItems] = useState([]);
  const [batchRunning, setBatchRunning] = useState(false);
  const [formatStage, setFormatStage] = useState('');
  const [activeItem, setActiveItem] = useState(null);
  const [downloadingWord, setDownloadingWord] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [formattedHtml, setFormattedHtml] = useState(null);
  const [structuredData, setStructuredData] = useState(null);
  const [showFormattedModal, setShowFormattedModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editHtml, setEditHtml] = useState('');
  const [previewVersion, setPreviewVersion] = useState(0);
  const [fontFamily, setFontFamily] = useState('Arial, sans-serif');
  const [fontSize, setFontSize] = useState('11px');
  const [lineSpacing, setLineSpacing] = useState('1.0');
  // Picker UI only — must not be written to body CSS on save (that tints the entire resume).
  const [textColorPicker, setTextColorPicker] = useState('#000000');
  const [bgColorPicker, setBgColorPicker] = useState('#ffffff');
  const savedEditorSelectionRef = useRef(null);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const editableContentRef = useRef(null);
  const originalHtmlRef = useRef(null);
  const originalWasFullDocRef = useRef(false);
  const originalHeadStylesRef = useRef('');
  const logoDataRef = useRef(null);
  const fileInputRef = useRef(null);
  const templateDropdownRef = useRef(null);

  const normalizeSingleResumeContent = (htmlString, treatAsFullDocument = false) => {
    if (!htmlString || typeof htmlString !== 'string') return htmlString;
    try {
      const parsed = new DOMParser().parseFromString(htmlString, 'text/html');
      const resumeContainers = parsed.body.querySelectorAll('.resume-container');
      if (resumeContainers.length > 1) {
        const firstNonEmpty =
          Array.from(resumeContainers).find((node) => (node.textContent || '').trim().length > 0) || resumeContainers[0];
        Array.from(resumeContainers).forEach((node) => {
          if (node !== firstNonEmpty) node.remove();
        });
      }
      return treatAsFullDocument
        ? `<!doctype html>\n${parsed.documentElement.outerHTML}`
        : parsed.body.innerHTML;
    } catch {
      return htmlString;
    }
  };

  useEffect(() => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      logoDataRef.current = {
        dataUrl: canvas.toDataURL('image/png'),
        aspectRatio: img.naturalWidth / img.naturalHeight,
      };
    };
    img.src = '/feuji-logo-header.png';
  }, []);

  // Drop legacy upload meta (older builds); we do not persist file metadata — it only caused repeat toasts after refresh.
  useEffect(() => {
    try {
      sessionStorage.removeItem(FILE_CACHE_KEY);
    } catch (e) {
      // ignore storage errors
    }
  }, []);

  // Fetch hardcoded format templates (Feuji1/2/3); fall back to constants if the fetch fails.
  useEffect(() => {
    const fallback = Object.values(TEMPLATE_FALLBACK);
    api.get(ENDPOINTS.RESUME.FORMAT_TEMPLATES, { timeout: 15000 })
      .then((resp) => {
        const list = resp?.data?.templates;
        setAvailableTemplates(Array.isArray(list) && list.length ? list : fallback);
      })
      .catch(() => setAvailableTemplates(fallback));
  }, []);

  // Lifetime count of resumes formatted through this tool, shown as a badge on the header.
  useEffect(() => {
    api.get(ENDPOINTS.RESUME.FORMAT_COUNT, { timeout: 15000 })
      .then((resp) => setFormatCount(resp?.data?.count ?? 0))
      .catch(() => setFormatCount(null));
  }, []);

    const templates = availableTemplates.length ? availableTemplates : Object.values(TEMPLATE_FALLBACK);
  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId) || null;

  // A usable template must be selected for the active path before formatting.
  const hasTemplateForPath = formatPath === 'existing' ? !!selectedTemplate : !!companyTemplate;

  /** Clear per-path employee selection/results when switching between template sources. */
  const resetBatchState = () => {
    setSelectedFiles([]);
    setBatchItems([]);
    setFormattedHtml(null);
    setStructuredData(null);
    setShowFormattedModal(false);
    setIsEditMode(false);
    setActiveItem(null);
    originalHtmlRef.current = null;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSwitchPath = (path) => {
    if (path === formatPath || batchRunning) return;
    resetBatchState();
    setFormatPath(path);
  };

  // Close the template dropdown on any outside click.
  useEffect(() => {
    if (!templateDropdownOpen) return undefined;
    const onDocMouseDown = (e) => {
      if (templateDropdownRef.current && !templateDropdownRef.current.contains(e.target)) {
        setTemplateDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [templateDropdownOpen]);

  const handleFileChange = (event) => {
    const picked = Array.from((event.target.files || []));
    // Reset the underlying input so picking the same filename again re-triggers onChange.
    event.target.value = '';
    if (!picked.length) return;

    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
    ];
    const accepted = [];
    picked.forEach((file) => {
      const lowerName = (file.name || '').toLowerCase();
      const extensionOk = lowerName.endsWith('.pdf') || lowerName.endsWith('.doc') || lowerName.endsWith('.docx');
      // Some browsers/OSes report PDF/DOC as application/octet-stream or empty string.
      const typeOk =
        allowedTypes.includes(file.type) ||
        (extensionOk && (!file.type || String(file.type).toLowerCase().includes('octet-stream')));

      if (!typeOk) {
        toast.error(`${file.name}: unsupported file. Please upload only PDF, DOC, or DOCX.`);
        return;
      }
      if (file.size > 200 * 1024 * 1024) {
        toast.error(`${file.name}: file size must be less than 200MB`);
        return;
      }
      if (selectedFiles.some((f) => f.name === file.name && f.size === file.size)) {
        return; // already selected
      }
      accepted.push(file);
    });

    if (accepted.length) {
      setSelectedFiles((prev) => [...prev, ...accepted]);
      // Any change to the selection invalidates previous batch results.
      setBatchItems([]);
      setFormattedHtml(null);
      setStructuredData(null);
      setShowFormattedModal(false);
      setIsEditMode(false);
      setActiveItem(null);
      originalHtmlRef.current = null;
    }
  };

  const handleRemoveSelectedFile = (index) => {
    if (batchRunning) return;
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setBatchItems([]);
    setFormattedHtml(null);
    setStructuredData(null);
    setShowFormattedModal(false);
    setIsEditMode(false);
    setActiveItem(null);
    originalHtmlRef.current = null;
    // Clear the underlying <input type="file"> so picking the same filename re-triggers onChange.
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /** User-friendly message for a per-file formatting failure. */
  const describeFormatError = (error, file) => {
    const status = error?.response?.status;
    const extracted = extractFormatterErrorMessage(error);
    const isPdfUpload =
      (file?.name || '').toLowerCase().endsWith('.pdf') ||
      String(file?.type || '').toLowerCase().includes('pdf');
    const mentionsPasswordProtected =
      /this pdf file is password protected/i.test(extracted) ||
      /password-protected pdf files are not supported/i.test(extracted) ||
      /password-protected pdf\b|password.?protected.*pdf|encrypted pdf|pdf.*\(encrypted\)/i.test(extracted);
    // Do not treat generic "Error reading PDF:" as password-protected; that often indicates a corrupt file.
    const mentionsCorruptedPdf =
      /the uploaded pdf file is invalid or corrupted/i.test(extracted) ||
      /invalid or corrupted pdf file/i.test(extracted) ||
      /unable to read the uploaded pdf file/i.test(extracted);

    if (status === 400 || (status === 502 && mentionsPasswordProtected)) {
      return isPdfUpload && mentionsCorruptedPdf
        ? INVALID_OR_CORRUPTED_PDF_MESSAGE
        : mentionsPasswordProtected
          ? PASSWORD_PROTECTED_PDF_MESSAGE
          : extracted || 'We could not format this file. Please check the file and try again.';
    }
    if (status === 413) {
      return 'File is too large for the server (HTTP 413). Try a smaller file or ask your admin to raise upload limits.';
    }
    if (status === 408) {
      return 'Your session timed out. Please retry this resume.';
    }
    if (status === 502 || status === 504) {
      return resolveApiErrorMessage(error, 'Formatter service is temporarily unavailable. Please retry.');
    }
    return resolveApiErrorMessage(error, 'Failed to format resume. Please try again.');
  };

  /** Process a single batch item through the pipeline for its kind. Returns { html, ... } or null. */
  const formatOneItem = async (item) => {
    setBatchItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, status: 'processing', error: null } : x)));
    try {
      let rawHtml = null;
      let structured = null;
      if (item.kind === 'company') {
        // Company template path: existing standardize endpoint (v1/v2/v3 routing was
        // resolved once when the template was selected and is carried on the item).
        const fd = new FormData();
        fd.append('file', item.file);
        const response = await api.post(item.endpoint, fd, { timeout: 900000 });
        rawHtml = response?.data?.html_content;
        const docxBase64 = response?.data?.docx_base64 || null;
        const pdfBase64 = response?.data?.pdf_base64 || null;
        if (!rawHtml || typeof rawHtml !== 'string' || !rawHtml.trim()) {
          const message = 'Standardization returned empty content. Please retry this file.';
          setBatchItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, status: 'failed', error: message } : x)));
          return null;
        }
        setBatchItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, status: 'completed', html: rawHtml, structuredData: null, docxBase64, pdfBase64 } : x)));
        setFormatCount((c) => (c == null ? c : c + 1));
        return { html: rawHtml, structuredData: null, docxBase64, pdfBase64 };
      }

      // Feuji path: existing per-file format pipeline.
      const formData = new FormData();
      formData.append('file', item.file);
      // Java endpoint reads @RequestParam("templateId") — camelCase matters
      formData.append('templateId', selectedTemplateId);

      const response = await api.post('/resumes/format-html', formData, {
        // Let the browser set multipart boundary — manual Content-Type breaks uploads
        timeout: 600000,
      });

      rawHtml = response?.data?.html_content;
      const previewHtml = sanitizeResumeHtml(rawHtml);
      structured = response?.data?.structured_data;

      if (!rawHtml || typeof rawHtml !== 'string' || !rawHtml.trim() ||
          !previewHtml || typeof previewHtml !== 'string' || !previewHtml.trim()) {
        const message = 'Resume formatter returned empty content. Please retry this file.';
        setBatchItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, status: 'failed', error: message } : x)));
        return null;
      }

      setBatchItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, status: 'completed', html: previewHtml, structuredData: structured ?? null } : x)));
      setFormatCount((c) => (c == null ? c : c + 1));
      return { html: previewHtml, structuredData: structured ?? null };
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || error?.isSessionExpired) {
        toast.error('Session expired. Please login again.');
        try {
          sessionStorage.setItem('auth_expired_reason', 'Session expired. Please login again.');
        } catch (e) {
          // best effort
        }
        setBatchItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, status: 'failed', error: 'Session expired. Please login again.' } : x)));
        setTimeout(() => {
          window.location.href = '/login';
        }, 800);
        return null;
      }
      const message = describeFormatError(error, item.file);
      setBatchItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, status: 'failed', error: message } : x)));
      return null;
    }
  };

  const handleFormatResume = async () => {
    if (batchRunning) return;

    if (!selectedFiles.length) {
      toast.error('Please select at least one resume file.');
      return;
    }

    if (formatPath === 'existing' && !selectedTemplate) {
      toast.error('Please select a template first.');
      return;
    }
    if (formatPath === 'own' && !companyTemplate) {
      toast.error('Please select one of your company templates (or extract a new one) first.');
      return;
    }

    const unavailable = selectedFiles.find((f) => !(f instanceof File) || f.size <= 0);
    if (unavailable) {
      toast.error('A selected file is no longer available. Please remove it and re-upload.');
      return;
    }

    const kind = formatPath === 'own' ? 'company' : 'feuji';
    const items = selectedFiles.map((file, i) => ({
      id: `${Date.now()}-${i}-${file.name}`,
      file,
      kind,
      endpoint: kind === 'company' ? companyTemplate.endpoint : null,
      status: 'queued',
      error: null,
      html: null,
      structuredData: null,
      docxBase64: null,
    }));
    setBatchItems(items);
    setFormattedHtml(null);
    setStructuredData(null);
    setActiveItem(null);
    setShowFormattedModal(false);
    setIsEditMode(false);
    originalHtmlRef.current = null;

    setBatchRunning(true);
    setFormatStage('Reading document & tables...');
    const stageTimer1 = setTimeout(() => {
      setFormatStage('Extracting skills, experience & projects...');
    }, 3000);
    const stageTimer2 = setTimeout(() => {
      setFormatStage('Formatting template & generating preview...');
    }, 18000);

    let completed = 0;
    try {
      // Controlled concurrency: process a few resumes at a time, the rest stay queued.
      const BATCH_CONCURRENCY = 2;
      let cursor = 0;
      const worker = async () => {
        while (cursor < items.length) {
          const item = items[cursor++];
          const result = await formatOneItem(item);
          if (result) completed += 1;
        }
      };
      await Promise.all(Array.from({ length: Math.min(BATCH_CONCURRENCY, items.length) }, worker));

      if (items.length === 1 && completed === 1) {
        // Single file: open the preview modal straight away (previous single-file behavior).
        const done = items[0];
        const result = await new Promise((resolve) => {
          setBatchItems((prev) => {
            resolve(prev.find((x) => x.id === done.id) || null);
            return prev;
          });
        });
        if (result?.html) {
          openItemPreview(result);
        }
        toast.success('Resume formatted successfully!');
      } else if (items.length > 1) {
        if (completed === items.length) {
          toast.success(`${completed} resumes formatted successfully!`);
        } else if (completed === 0) {
          toast.error('All resumes failed to format. Please retry.');
        } else {
          toast.warning(`${completed}/${items.length} resumes formatted — retry the failed ones below.`);
        }
      }
    } finally {
      clearTimeout(stageTimer1);
      clearTimeout(stageTimer2);
      setFormatStage('');
      setBatchRunning(false);
    }
  };

  const handleRetryItem = async (id) => {
    if (batchRunning) return;
    const item = batchItems.find((x) => x.id === id);
    if (!item || item.status === 'processing') return;

    setBatchRunning(true);
    setFormatStage('Reading document & tables...');
    const stageTimer1 = setTimeout(() => {
      setFormatStage('Extracting skills, experience & projects...');
    }, 3000);
    const stageTimer2 = setTimeout(() => {
      setFormatStage('Formatting template & generating preview...');
    }, 18000);

    try {
      const result = await formatOneItem(item);
      if (result) {
        toast.success(`${item.file.name} formatted successfully!`);
      }
    } finally {
      clearTimeout(stageTimer1);
      clearTimeout(stageTimer2);
      setFormatStage('');
      setBatchRunning(false);
    }
  };

  const openItemPreview = (item) => {
    if (!item?.html) return;
    setActiveItem(item);
    setFormattedHtml(item.html);
    setStructuredData(item.structuredData);
    setPreviewVersion((v) => v + 1);
    setIsEditMode(false);
    setShowFormattedModal(true);
  };

  /** Closing the modal must exit edit mode; otherwise reopening shows an empty contentEditable instead of the iframe preview. */
  const handleCloseFormattedModal = () => {
    setShowFormattedModal(false);
    setIsEditMode(false);
    setEditHtml('');
    if (editableContentRef.current) {
      editableContentRef.current.innerHTML = '';
    }
  };

  const previewSrcDoc = useMemo(() => {
    if (!formattedHtml || typeof formattedHtml !== 'string') return '';
    const previewOnlyHtml = buildPreviewOnlyHtml(formattedHtml);
    const trimmed = (previewOnlyHtml || '').trim();
    const isFullDocument = /^<!DOCTYPE|^<html[\s>]/i.test(trimmed);
    if (isFullDocument) {
      return trimmed;
    }
    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      html, body { margin: 0; padding: 0; background: #ffffff; }
      /* Ensure long lines wrap inside iframe */
      * { box-sizing: border-box; }
    </style>
  </head>
  <body>
    ${previewOnlyHtml}
  </body>
</html>`;
  }, [formattedHtml]);

  // Helper function to convert image to base64 for Word document
  const imageToBase64 = (imagePath) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          const dataURL = canvas.toDataURL('image/png');
          resolve(dataURL);
        } catch (error) {
          console.warn('Failed to convert image to base64:', error);
          resolve('');
        }
      };
      img.onerror = () => {
        // If image fails to load, return empty string
        resolve('');
      };
      // Try to load from public folder
      img.src = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    });
  };

  const convertHtmlForWord = (html) => {
    const div = document.createElement('div');
    div.innerHTML = html;

    const pageBgFromContainer = getResumePageBackgroundColor(div);

    // Strip embedded <style> blocks from AI service HTML —
    // the Word template has its own CSS; the embedded flex/pseudo-element
    // rules break Word rendering
    div.querySelectorAll('style').forEach((s) => s.remove());

    // Remove nested .resume-container / .resume-content wrappers that duplicate
    // the Word template's own wrappers (avoids double padding)
    div.querySelectorAll('.resume-container').forEach((rc) => {
      rc.replaceWith(...rc.childNodes);
    });
    div.querySelectorAll('.resume-content').forEach((rc) => {
      rc.replaceWith(...rc.childNodes);
    });

    // Remove the header div from body content — it will be in Word's
    // repeating page header instead (see mso-element:header in the template)
    div.querySelectorAll('.header').forEach((header) => header.remove());

    // Convert flex-based .align-row (and any leftover .exp-field-table) to Word-compatible tables
    const toWordFieldTable = (labelText, valueHtml, labelPct, valuePct) => {
      const table = document.createElement('table');
      table.setAttribute('cellpadding', '0');
      table.setAttribute('cellspacing', '0');
      table.setAttribute('border', '0');
      table.style.cssText =
        'width:100%;border:0;border-collapse:collapse;margin:0 0 3px 0;font-size:11px;line-height:1.5;mso-table-lspace:0pt;mso-table-rspace:0pt;';

      const tr = document.createElement('tr');

      const tdLabel = document.createElement('td');
      tdLabel.style.cssText = `width:${labelPct};font-weight:bold;vertical-align:top;padding:0;white-space:nowrap;`;
      tdLabel.textContent = labelText;

      const tdColon = document.createElement('td');
      tdColon.style.cssText = 'width:3%;font-weight:bold;vertical-align:top;padding:0 6px 0 0;white-space:nowrap;';
      tdColon.textContent = ':';

      const tdValue = document.createElement('td');
      tdValue.style.cssText = `width:${valuePct};vertical-align:top;padding:0;`;
      tdValue.innerHTML = valueHtml || '';

      tr.appendChild(tdLabel);
      tr.appendChild(tdColon);
      tr.appendChild(tdValue);
      table.appendChild(tr);
      return table;
    };

    div.querySelectorAll('.align-row').forEach((row) => {
      const label = row.querySelector('.align-label-skills, .align-label-exp');
      const value = row.querySelector('.align-value');
      if (!label) return;

      const isSkills = label.classList.contains('align-label-skills');
      const labelPct = isSkills ? '26%' : '20%';
      const valuePct = isSkills ? '71%' : '77%';
      row.replaceWith(
        toWordFieldTable(
          label.textContent,
          value ? value.innerHTML : '',
          labelPct,
          valuePct
        )
      );
    });

    // Legacy Work/Project table rows (if any remain)
    div.querySelectorAll('.exp-field-table').forEach((table) => {
      const label = table.querySelector('.exp-field-label');
      const value = table.querySelector('.exp-field-value');
      if (!label) return;
      table.replaceWith(
        toWordFieldTable(
          label.textContent,
          value ? value.innerHTML : '',
          '20%',
          '77%'
        )
      );
    });

    // Word ignores list-style:none on <ul> — replace <ul> with <div> containers
    // so Word doesn't add its own disc bullets
    div.querySelectorAll('ul.resp-bullets, ul.bullet-list, ul.exp-field-list').forEach((ul) => {
      const container = document.createElement('div');
      const isExpFields = ul.classList.contains('exp-field-list');
      container.style.cssText = isExpFields
        ? 'margin-left:0;margin-bottom:4px;margin-top:2px;padding:0;'
        : 'margin-left:20px;margin-bottom:6px;margin-top:2px;padding:0;';
      while (ul.firstChild) {
        const li = ul.firstChild;
        if (li.nodeType === 1 && li.tagName === 'LI') {
          if (isExpFields) {
            // Keep align-row tables conversion (already done above); just unwrap li content.
            const wrap = document.createElement('div');
            wrap.style.cssText = 'margin:0 0 4px 0;padding:0;';
            wrap.innerHTML = li.innerHTML;
            container.appendChild(wrap);
          } else {
            const p = document.createElement('p');
            const liLineHeight = li.style?.lineHeight;
            p.style.cssText =
              `margin:0 0 4px 0;padding:0;font-size:11px;line-height:${liLineHeight || '1.55'};`;
            const text = li.textContent.startsWith('\u2022') ? li.innerHTML : '\u2022 ' + li.innerHTML;
            p.innerHTML = text;
            container.appendChild(p);
          }
        }
        ul.removeChild(li);
      }
      ul.replaceWith(container);
    });

    return { html: div.innerHTML, pageBg: pageBgFromContainer };
  };

  const handleDownloadWord = async (arg) => {
    // Only accept a real batch item (the modal footer passes the click event when used as a direct handler).
    const target = arg && arg.file && arg.id ? arg : activeItem;
    if (!target?.structuredData && !target?.docxBase64 && !target?.html && !formattedHtml) {
      toast.error('No formatted content available. Please format a resume first.');
      return;
    }

    try {
      if (downloadingWord || downloadingPdf) return;
      setDownloadingWord(true);

      const baseName = target.file ? target.file.name.replace(/\.[^/.]+$/, '') : 'resume';

      // ── Company template path: the standardize response already carries the
      // server-rendered DOCX (base64) — download it directly, no new renderer.
      if (target.kind === 'company') {
        if (!target.docxBase64) {
          toast.error('No Word document is available for this resume. Please retry formatting it.');
          return;
        }
        const blob = base64ToBlob(target.docxBase64, 'application/vnd.openxmlformats-officedocument.wordprocessingml.document');
        triggerBlobDownload(blob, `${baseName}_standardized.docx`);
        toast.dismiss('resume-download-pdf');
        toast.success('Word document downloaded successfully!', { toastId: 'resume-download-word' });
        return;
      }

      // ── Feuji path: call the real DOCX backend \u2014 returns proper python-docx generated .docx bytes
      const response = await api.post(ENDPOINTS.RESUME.FORMAT_FROM_DATA(selectedTemplateId), target.structuredData, {
        responseType: 'blob',
        timeout: 120000,
      });

      const filename = `${baseName}_formatted.docx`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.dismiss('resume-download-pdf');
      toast.success('Word document downloaded successfully!', { toastId: 'resume-download-word' });
    } catch (error) {
      console.error('Error generating Word document:', error);
      toast.dismiss('resume-download-word');
      toast.error('Failed to generate Word document. Please try again.', { toastId: 'resume-download-word' });
    } finally {
      setDownloadingWord(false);
    }
  };

  const handleEditMode = () => {
    if (!formattedHtml) return;
    originalHtmlRef.current = formattedHtml;
    originalWasFullDocRef.current = isFullHtmlDocument(formattedHtml);
    originalHeadStylesRef.current = getHeadStylesText(formattedHtml);

    let bodyHtml = getBodyHtmlForEditing(formattedHtml);
    // Convert Work/Project flex rows → stable tables before editing (skills untouched).
    const editDoc = parseHtmlDocument(
      `<!doctype html><html><head></head><body>${bodyHtml}</body></html>`
    );
    if (editDoc?.body) {
      fixExperienceSummaryFieldLayout(editDoc.body.querySelector('.resume-container') || editDoc.body);
      bodyHtml = editDoc.body.innerHTML;
    }
    // Use scoped CSS so font changes don't leak to the main app; strip ALL body/html rules from
    // the resume's own head styles to prevent layout corruption on the host page.
    const userCss = buildEditorScopedCss(fontFamily, fontSize);
    const headStylesForEdit = stripAllBodyHtmlStyles(originalHeadStylesRef.current || '');
    const injected = `<style id="resume-formatter-edit-style">
${headStylesForEdit}
${userCss}
</style>${bodyHtml}`;


    setEditHtml(injected || '');
    setIsEditMode(true);
    setTimeout(() => {
      if (editableContentRef.current) {
        editableContentRef.current.innerHTML = injected;
        const container = editableContentRef.current.querySelector('.resume-container');
        if (container) {
          const inlineBg = container.style.backgroundColor;
          const computedBg =
            typeof window !== 'undefined' && window.getComputedStyle
              ? window.getComputedStyle(container).backgroundColor
              : '';
          const hex = cssColorToHex(inlineBg || computedBg);
          if (hex) setBgColorPicker(hex);
        }
        // Do not auto-focus: avoids the blue focus ring until the user clicks to edit.
      }
    }, 100);
  };

  const handleSaveEdit = () => {
    if (!editableContentRef.current) return;

    // IMPORTANT: ContentEditable can accidentally contain duplicated blocks after edits.
    // To ensure "Save" modifies the existing resume (instead of appending a second copy),
    // capture ONLY the first `.resume-container` from the live DOM.
    const cloned = editableContentRef.current.cloneNode(true);
    cloned.querySelectorAll('#resume-formatter-edit-style').forEach((n) => n.remove());
    // ContentEditable edits can accidentally delete the dedicated ":" span in skill rows.
    // Canonicalize Technical Skills rows before persisting so formatting stays consistent after Save.
    canonicalizeTechnicalSkillsSectionInPlace(cloned);
    // Work/Project Summary: keep structure like Technical Skills; do not strip
    // Enter/<br> newlines from field values.
    fixExperienceSummaryFieldLayout(cloned);

    const firstResumeContainer = cloned.querySelector('.resume-container');
    const pickFirstMeaningfulBlock = () => {
      if (firstResumeContainer) return firstResumeContainer.outerHTML;
      const children = Array.from(cloned.children || []).filter((el) => el && el.tagName !== 'STYLE');
      if (children.length === 0) return cloned.innerHTML;
      const candidate =
        children.find((el) => ((el.textContent || '').replace(/\s+/g, ' ').trim().length > 400)) || children[0];
      return candidate ? candidate.outerHTML : cloned.innerHTML;
    };

    const rawBodyHtml = pickFirstMeaningfulBlock();
    const newBodyHtml = toBodyInnerHtml(rawBodyHtml);
    // When the editor still has exactly one resume block, skip string-based `dedupeResumeBodyHtml`
    // and final `hardTrimToSingleResumeFragment` — they mis-fire on legitimate edited HTML (repeated
    // headings/colors) and produced empty saves. Heavy cleanup stays for multi-block / messy DOM.
    const resumeNodesInClone = cloned.querySelectorAll('.resume-container');
    const useLightSave =
      resumeNodesInClone.length === 1 &&
      firstResumeContainer &&
      resumeNodesInClone[0] === firstResumeContainer;

    let primaryBodyHtml;
    if (useLightSave) {
      // Single known-good block: persist what the user edited without extract/trim heuristics
      // that mis-detect duplicates inside one container (e.g. repeated section titles).
      primaryBodyHtml = firstResumeContainer.outerHTML;
    } else {
      const cleanBodyHtml = dedupeResumeBodyHtml(newBodyHtml);
      primaryBodyHtml = extractSingleResumeFromEditedHtml(cleanBodyHtml);
    }

    const bodyFragment = useLightSave
      ? primaryBodyHtml
      : hardTrimToSingleResumeFragment(primaryBodyHtml);

    const userCss = buildUserEditCss(fontFamily, fontSize);
    const headStylesForSave = stripGlobalBodyColorFromStyles(originalHeadStylesRef.current || '');

    const visibleTextLen = (html) => {
      if (!html || typeof html !== 'string') return 0;
      const t = html.trim();
      const wrap = /^<!DOCTYPE|^<html[\s>]/i.test(t)
        ? t
        : `<!doctype html><html><head></head><body>${t}</body></html>`;
      try {
        const d = new DOMParser().parseFromString(wrap, 'text/html');
        return (d.body && d.body.textContent ? d.body.textContent : '')
          .replace(/\s+/g, ' ')
          .trim().length;
      } catch {
        return t.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().length;
      }
    };

    let nextHtml;
    if (originalWasFullDocRef.current && originalHtmlRef.current) {
      const rebuilt = buildSingleResumeHtmlDocument({
        headStylesText: headStylesForSave,
        userCssText: userCss,
        bodyHtml: bodyFragment,
      });
      // Do not run sanitizeResumeHtml here: it re-applies dedupe/trim on body and can wipe
      // legitimate content after edits (double-pass false positives). Initial API load still sanitizes once.
      nextHtml = normalizeSingleResumeContent(rebuilt, true);
    } else {
      nextHtml = normalizeSingleResumeContent(`<style>${userCss}</style>${bodyFragment}`, false);
    }

    const beforeLen = visibleTextLen(formattedHtml || '');
    const afterLen = visibleTextLen(nextHtml);
    if (beforeLen > 120 && afterLen < 40) {
      toast.error('Save would clear almost all content, so it was cancelled. Try smaller edits or use Cancel.');
      return;
    }

    originalHtmlRef.current = nextHtml;
    setFormattedHtml(nextHtml);
    setPreviewVersion((v) => v + 1);

    setIsEditMode(false);
    setEditHtml('');
    toast.success('Resume updated successfully!');
  };

  const handleCancelEdit = () => {
    // Restore original HTML
    if (originalHtmlRef.current) {
      setFormattedHtml(originalHtmlRef.current);
      setPreviewVersion((v) => v + 1);
    }
    setIsEditMode(false);
    setEditHtml('');
    if (editableContentRef.current) {
      editableContentRef.current.innerHTML = '';
    }
  };

  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    if (editableContentRef.current) {
      editableContentRef.current.focus();
    }

    // Update button states
    if (command === 'bold') {
      setIsBold(document.queryCommandState('bold'));
    } else if (command === 'italic') {
      setIsItalic(document.queryCommandState('italic'));
    } else if (command === 'underline') {
      setIsUnderline(document.queryCommandState('underline'));
    } else if (command === 'strikeThrough') {
      setIsStrikethrough(document.queryCommandState('strikeThrough'));
    }
  };

  const handleFontFamilyChange = (e) => {
    const font = e.target.value;
    setFontFamily(font);
    // Extract font name (remove quotes and fallback fonts)
    const fontName = font.split(',')[0].trim().replace(/['"]/g, '');
    execCommand('fontName', fontName);
  };

  const handleFontSizeChange = (e) => {
    const size = e.target.value;
    setFontSize(size);
    // Apply fontSize via CSS using execCommand with formatBlock or creating a span
    if (editableContentRef.current) {
      const selection = window.getSelection();
      if (selection.rangeCount > 0 && !selection.isCollapsed) {
        const range = selection.getRangeAt(0);
        if (editableContentRef.current.contains(range.commonAncestorContainer)) {
          applyInlineStyleToRange(range, 'fontSize', size);
        }
        editableContentRef.current.focus();
      } else {
        // If no selection, just update the style for future typing
        document.execCommand('styleWithCSS', false, true);
        document.execCommand('fontSize', false, '3');
      }
    }
  };

  const handleLineSpacingChange = (e) => {
    const spacing = e.target.value;
    setLineSpacing(spacing);
    const root = editableContentRef.current;
    if (!root) return;
    restoreEditorSelection();
    applyLineSpacingToSelection(root, spacing);
  };

  const captureEditorSelection = () => {
    const root = editableContentRef.current;
    if (!root) return;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (!root.contains(range.commonAncestorContainer)) return;
    savedEditorSelectionRef.current = range.cloneRange();
  };

  const restoreEditorSelection = () => {
    const range = savedEditorSelectionRef.current;
    if (!range) return false;
    const selection = window.getSelection();
    if (!selection) return false;
    selection.removeAllRanges();
    selection.addRange(range);
    return true;
  };

  const applyStyleToEditorSelection = (styleProp, value, execFallback) => {
    const root = editableContentRef.current;
    if (!root) return;
    restoreEditorSelection();
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    if (!root.contains(range.commonAncestorContainer)) return;

    if (range.collapsed) {
      document.execCommand('styleWithCSS', false, true);
      document.execCommand(execFallback, false, value);
      root.focus();
      return;
    }

    applyInlineStyleToRange(range, styleProp, value);
    selection.removeAllRanges();
    root.focus();
  };

  const handleFontColorChange = (e) => {
    const color = e.target.value;
    setTextColorPicker(color);
    applyStyleToEditorSelection('color', color, 'foreColor');
  };

  const applyResumePageBackgroundColor = (color) => {
    const root = editableContentRef.current;
    if (!root || !color) return;
    applyResumePageBackgroundToPrintRoot(root, color);
  };

  const handleBackgroundColorChange = (e) => {
    const color = e.target.value;
    setBgColorPicker(color);
    applyResumePageBackgroundColor(color);
  };

  const handleToggleBold = () => {
    execCommand('bold');
    setIsBold(!isBold);
  };

  const handleToggleItalic = () => {
    execCommand('italic');
    setIsItalic(!isItalic);
  };

  const handleToggleUnderline = () => {
    execCommand('underline');
    setIsUnderline(!isUnderline);
  };

  const handleAlignLeft = () => execCommand('justifyLeft');
  const handleAlignCenter = () => execCommand('justifyCenter');
  const handleAlignRight = () => execCommand('justifyRight');
  const handleAlignJustify = () => execCommand('justifyFull');

  const handleToggleStrikethrough = () => {
    execCommand('strikeThrough');
    setIsStrikethrough(!isStrikethrough);
  };

  const handleInsertUnorderedList = () => execCommand('insertUnorderedList');
  const handleInsertOrderedList = () => execCommand('insertOrderedList');
  const handleIndent = () => execCommand('indent');
  const handleOutdent = () => execCommand('outdent');
  const handleSubscript = () => execCommand('subscript');
  const handleSuperscript = () => execCommand('superscript');
  const handleClearFormatting = () => execCommand('removeFormat');

  const handleInsertBullet = () => {
    if (editableContentRef.current) {
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const bullet = document.createTextNode('• ');
        range.insertNode(bullet);
        range.setStartAfter(bullet);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        editableContentRef.current.focus();
      }
    }
  };

  useEffect(() => {
    // Update button states based on current selection
    const updateButtonStates = () => {
      if (isEditMode) {
        try {
          setIsBold(document.queryCommandState('bold'));
          setIsItalic(document.queryCommandState('italic'));
          setIsUnderline(document.queryCommandState('underline'));
          setIsStrikethrough(document.queryCommandState('strikeThrough'));
        } catch (e) {
          // Ignore errors
        }
      }
    };

    const el = isEditMode ? editableContentRef.current : null;
    if (isEditMode && el) {
      // Initialize content when entering edit mode
      if (editHtml && el.innerHTML !== editHtml) {
        el.innerHTML = editHtml;
      }

      document.addEventListener('selectionchange', updateButtonStates);
      el.addEventListener('mouseup', updateButtonStates);
      el.addEventListener('keyup', updateButtonStates);
    }

    return () => {
      document.removeEventListener('selectionchange', updateButtonStates);
      if (el) {
        el.removeEventListener('mouseup', updateButtonStates);
        el.removeEventListener('keyup', updateButtonStates);
      }
    };
  }, [isEditMode, editHtml]);
  const handleDownloadPDF = async (arg) => {
    // Only accept a real batch item (the modal footer passes the click event as a direct handler).
    const target = arg && arg.file && arg.id ? arg : activeItem;
    if (!formattedHtml && !target?.html) {
      toast.error('No content available for PDF generation');
      return;
    }

    try {
      if (downloadingWord || downloadingPdf) return;
      setDownloadingPdf(true);

      const baseName = target.file ? target.file.name.replace(/\.[^/.]+$/, '') : 'resume';

      // ── Company template path: download the authoritative server-rendered PDF
      // (LibreOffice document flow with repeating headers), falling back to client painter only if absent.
      if (target.kind === 'company') {
        if (target.pdfBase64) {
          toast.dismiss('resume-download-word');
          const blob = base64ToBlob(target.pdfBase64, 'application/pdf');
          triggerBlobDownload(blob, `${baseName}_standardized.pdf`);
          toast.dismiss('resume-download-pdf');
          toast.success('PDF downloaded successfully!', { toastId: 'resume-download-pdf' });
          return;
        }
        if (!target.html) {
          toast.error('No content available for PDF generation');
          return;
        }
        toast.dismiss('resume-download-word');
        toast.loading('Generating PDF...', { toastId: 'resume-download-pdf' });
        try {
          await downloadStandardizedPdf(target.html, `${baseName}_standardized.pdf`);
          toast.dismiss('resume-download-pdf');
          toast.success('PDF downloaded successfully!', { toastId: 'resume-download-pdf' });
        } catch (paintErr) {
          console.error('Standardized PDF generation error:', paintErr);
          toast.dismiss('resume-download-pdf');
          toast.error('Failed to generate PDF', { toastId: 'resume-download-pdf' });
        }
        return;
      }

      // ── Feuji2/Feuji3/Feuji4: authoritative PDF via the document-native server path ──
      // structured data + template → DOCX renderer → LibreOffice headless → PDF.
      // Text stays selectable and vertical geometry is laid out by a document
      // engine; no html2canvas bitmap slicing/rescaling in the browser.
      if (selectedTemplateId === 'feuji2' || selectedTemplateId === 'feuji3' ||
          selectedTemplateId === 'feuji4') {
        if (!target?.structuredData && !target?.html && !formattedHtml) {
          setDownloadingPdf(false);
          toast.error('No formatted content available. Please format a resume first.');
          return;
        }
        const response = await api.post(
          ENDPOINTS.RESUME.FORMAT_PDF_FROM_DATA(selectedTemplateId),
          target.structuredData,
          { responseType: 'blob', timeout: 180000 }
        );
        const filename = `${baseName}_formatted.pdf`;
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.dismiss('resume-download-word');
        toast.success('PDF downloaded successfully!', { toastId: 'resume-download-pdf' });
        return;
      }

      // ── Feuji1: existing known-good client-side export (regression baseline) ──
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth(); // 210
      const pageHeight = pdf.internal.pageSize.getHeight(); // 297
      const isFeuji1 = selectedTemplateId === 'feuji1';
      const isFeuji4 = selectedTemplateId === 'feuji4';
      const hasBarHeader = isFeuji1 || isFeuji4;
      const headerHeightMm = 9;
      const headerContentGapMm = 2.5;
      const contentStartMm = headerHeightMm + headerContentGapMm;
      const footerHeightMm = 12;
      const contentAreaMm = pageHeight - contentStartMm - footerHeightMm;
      const continuationTopPadMm = 2;
      // Feuji2/3: real page margins + no orange bar (logo only, top-left, every page)
      const sideMarginMm = isFeuji1 ? 0 : 14;
      const contentWidthMm = pageWidth - sideMarginMm * 2;
      const mmToDp = 96 / 25.4; // css px per mm at 96dpi
      const sideMarginPx = Math.round(sideMarginMm * mmToDp);
      const A4_WIDTH_PX = isFeuji1 ? 794 : 794 - sideMarginPx * 2;

      let htmlToPrint = target?.html || formattedHtml;
      if (isFeuji4) {
        // Server HTML already contains the orange bar + logo on page 1; the
        // per-page painted header below replaces it (matches the repeating
        // Word header of the reference file).
        htmlToPrint = htmlToPrint.replace(
          /<div style="background:#EC7C30;[\s\S]*?<\/div>/i, '');
      } else if (!isFeuji1) {
        // The server HTML embeds the logo at the top of page 1; the per-page
        // header logo painted below replaces it (samples repeat it on every page).
        htmlToPrint = htmlToPrint.replace(
          /<img[^>]+src="data:image\/png;base64,[^"]+"[^>]*>/i, '');
      }
      if (isEditMode && editableContentRef.current) {
        htmlToPrint = editableContentRef.current.innerHTML;

        // Defensive: if the editor injected a global body color/background rule,
        // remove ONLY those global declarations before rendering to PDF.
        // (Selection colors are inline and must remain.)
        try {
          const tmp = new DOMParser().parseFromString(
            `<!doctype html><html><head></head><body>${htmlToPrint}</body></html>`,
            'text/html'
          );
          const styleEl = tmp.getElementById('resume-formatter-edit-style');
          if (styleEl && typeof styleEl.textContent === 'string') {
            // Remove global body color/background-color declarations (keep the rest of formatter CSS).
            styleEl.textContent = styleEl.textContent
              .replace(/color\s*:\s*#[0-9a-fA-F]{3,8}\s*;?/g, '')
              .replace(/background-color\s*:\s*#[0-9a-fA-F]{3,8}\s*;?/g, '');
          }
          htmlToPrint = tmp.body ? tmp.body.innerHTML : htmlToPrint;
        } catch {
          // ignore
        }
      }

      // Render HTML off-screen at a fixed A4 width
      const container = document.createElement('div');
      container.style.position = 'absolute';
      container.style.left = '-9999px';
      container.style.top = '0';
      container.style.width = `${A4_WIDTH_PX}px`;
      container.style.padding = '0';
      container.style.margin = '0';
      container.innerHTML = htmlToPrint;

      const pageBg = getResumePageBackgroundColor(container);
      const pageBgRgb = hexToRgb(pageBg);
      applyResumePageBackgroundToPrintRoot(container, pageBg);
      injectPdfExportStyles(container);

      document.body.appendChild(container);
      // Force layout so PDF-only styles (bullets, padding) are measured before capture.
      void container.offsetHeight;

      // Collect safe breakpoints for pagination (section/item boundaries + bullets)
      const resumeRoot = container.querySelector('.resume-container') || container;
      const breakpointData = collectPdfBreakpointData(resumeRoot);

      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: pageBg,
        width: A4_WIDTH_PX,
        windowWidth: A4_WIDTH_PX,
      });

      document.body.removeChild(container);

      // Convert mm page content height to pixels at the captured canvas scale
      const mmToPxOnCanvas = (canvas.width / pageWidth);
      const pageContentHeightPx = Math.max(1, Math.floor(contentAreaMm * mmToPxOnCanvas));

      const totalHeightPx = canvas.height;
      let yOffset = 0;
      let pageNum = 0;
      const canvasScale = 2;

      while (yOffset < totalHeightPx) {
        if (pageNum > 0) pdf.addPage();
        pageNum++;

        const idealEnd = Math.min(yOffset + pageContentHeightPx, totalHeightPx);
        const endY = pickPdfPageEnd(breakpointData, yOffset, idealEnd, totalHeightPx, canvasScale);
        const sliceHeightPx = Math.max(1, endY - yOffset);
        const topPadPx = pageNum > 1
          ? Math.max(1, Math.floor(continuationTopPadMm * mmToPxOnCanvas))
          : 0;

        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = sliceHeightPx + topPadPx;
        const ctx = pageCanvas.getContext('2d');
        ctx.fillStyle = pageBg;
        ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        ctx.drawImage(
          canvas,
          0, yOffset, canvas.width, sliceHeightPx,
          0, topPadPx, canvas.width, sliceHeightPx
        );

        const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.95);
        const sliceHeightMm = (sliceHeightPx + topPadPx) / mmToPxOnCanvas;

        // Header — Feuji1/Feuji4: colored bar + right-aligned logo.
        // Feuji2/3: white strip with small Feuji logo top-left (matches samples).
        if (isFeuji1) {
          pdf.setFillColor(243, 118, 45);
          pdf.rect(0, 0, pageWidth, headerHeightMm, 'F');
          if (logoDataRef.current) {
            const logoH = headerHeightMm;
            const logoW = logoH * logoDataRef.current.aspectRatio;
            const logoX = pageWidth - logoW - 1.5;
            pdf.addImage(logoDataRef.current.dataUrl, 'PNG', logoX, 0, logoW, logoH);
          }
        } else if (isFeuji4) {
          pdf.setFillColor(236, 124, 48);
          pdf.rect(0, 0, pageWidth, headerHeightMm, 'F');
          if (logoDataRef.current) {
            const logoH = headerHeightMm - 1;
            const logoW = logoH * logoDataRef.current.aspectRatio;
            const logoX = pageWidth - logoW - 1.5;
            pdf.addImage(logoDataRef.current.dataUrl, 'PNG', logoX, 0.5, logoW, logoH);
          }
        } else {
          if (pageBgRgb.r || pageBgRgb.g || pageBgRgb.b) {
            pdf.setFillColor(pageBgRgb.r, pageBgRgb.g, pageBgRgb.b);
            pdf.rect(0, 0, pageWidth, contentStartMm, 'F');
          }
          if (logoDataRef.current) {
            const logoH = headerHeightMm - 1;
            const logoW = logoH * logoDataRef.current.aspectRatio;
            pdf.addImage(logoDataRef.current.dataUrl, 'PNG', sideMarginMm, 1, logoW, logoH);
          }
        }

        // Fill slice band and extend page background below content when the slice is shorter than the page.
        pdf.setFillColor(pageBgRgb.r, pageBgRgb.g, pageBgRgb.b);
        pdf.rect(0, contentStartMm, pageWidth, sliceHeightMm, 'F');
        if (sliceHeightMm < contentAreaMm) {
          pdf.rect(0, contentStartMm + sliceHeightMm, pageWidth, contentAreaMm - sliceHeightMm, 'F');
        }

        // Content slice — Feuji1 full-bleed; Feuji2/3 inset by side margins
        pdf.addImage(pageImgData, 'JPEG', sideMarginMm, contentStartMm, contentWidthMm, sliceHeightMm);

        // Footer page number
        pdf.setTextColor(0, 0, 0);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`${pageNum}`, pageWidth / 2, pageHeight - 5, { align: 'center' });

        yOffset += sliceHeightPx;
      }

      const filename = (target?.file || activeItem?.file)?.name?.replace(/\.[^/.]+$/, '') || 'resume';
      pdf.save(`${filename}_formatted.pdf`);
      toast.dismiss('resume-download-word');
      toast.success('PDF downloaded successfully!', { toastId: 'resume-download-pdf' });
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.dismiss('resume-download-pdf');
      toast.error('Failed to generate PDF', { toastId: 'resume-download-pdf' });
    } finally {
      setDownloadingPdf(false);
    }
  };



  const tbBtn = (active) => ({
    padding: '3px 8px', fontSize: 12, borderRadius: 5, cursor: 'pointer', outline: 'none',
    fontFamily: 'inherit', transition: 'all 0.15s',
    border: `1.5px solid ${active ? '#0041C2' : 'var(--ds-border)'}`,
    background: active ? '#0041C2' : 'var(--ds-input-bg)',
    color: active ? '#fff' : 'var(--ds-text)',
  });

  const footBtn = (grad, shadow) => ({
    padding: '7px 14px', fontSize: 12, fontWeight: 600, borderRadius: 8,
    border: 'none', cursor: 'pointer', fontFamily: 'inherit',
    background: grad, color: '#fff',
    boxShadow: shadow, transition: 'all 0.18s', outline: 'none',
    display: 'inline-flex', alignItems: 'center', gap: 6,
  });

  return (
    <div style={{ padding: '4px 0 20px' }}>
      {/* ── Single Format Resume card ──────────────────────────────────────── */}
      <div style={{ maxWidth: 680, margin: '0 auto', background: 'var(--ds-card)', border: '1px solid var(--ds-border)', borderRadius: 12, boxShadow: 'var(--ds-shadow-md)' }}>
        <div style={{ padding: '14px 18px', background: 'linear-gradient(135deg,#0041C2,#1A5FD4)', display: 'flex', alignItems: 'center', gap: 12, borderTopLeftRadius: 12, borderTopRightRadius: 12 }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <i className="fas fa-magic" style={{ color: '#fff', fontSize: 14 }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#fff' }}>Format Resume</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.82)', marginTop: 2 }}>Choose a template, upload employee resumes and format them in one place</div>
          </div>
          {formatCount != null && (
            <div
              title="Resumes formatted to date"
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 10px', borderRadius: 20, background: 'rgba(255,255,255,0.18)', flexShrink: 0 }}
            >
              <i className="fas fa-file-circle-check" style={{ color: '#fff', fontSize: 12 }} />
              <span style={{ fontWeight: 700, fontSize: 13, color: '#fff' }}>{formatCount.toLocaleString()}</span>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.82)' }}>formatted</span>
            </div>
          )}
        </div>
        <div style={{ padding: 18 }}>
        {/* Step 1 — how would you like to format the resumes? */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontWeight: 700, fontSize: 12, color: 'var(--ds-text)', marginBottom: 6 }}>How would you like to format the resumes?</label>
      <div style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
        <div
          onClick={() => handleSwitchPath('existing')}
          style={{ flex: 1, padding: '13px 15px', borderRadius: 12, cursor: 'pointer', fontFamily: 'inherit',
            border: `1.5px solid ${formatPath === 'existing' ? '#0041C2' : 'var(--ds-border)'}`,
            background: formatPath === 'existing' ? 'rgba(0,65,194,0.07)' : 'var(--ds-card)',
            boxShadow: formatPath === 'existing' ? '0 4px 14px rgba(0,65,194,0.18)' : 'var(--ds-shadow-md)' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <i className="fas fa-layer-group" style={{ color: formatPath === 'existing' ? '#0041C2' : 'var(--ds-text2)', fontSize: 14 }} />
            <span style={{ fontWeight: 700, fontSize: 13, color: formatPath === 'existing' ? '#0041C2' : 'var(--ds-text)' }}>Choose Existing Template</span>
            {formatPath === 'existing' && <i className="fas fa-check-circle" style={{ marginLeft: 'auto', color: '#16a34a', fontSize: 13 }} />}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--ds-text2)' }}>Use one of Feuji&apos;s standard templates.</div>
        </div>
        <div
          onClick={() => handleSwitchPath('own')}
          style={{ flex: 1, padding: '13px 15px', borderRadius: 12, cursor: 'pointer', fontFamily: 'inherit',
            border: `1.5px solid ${formatPath === 'own' ? '#0041C2' : 'var(--ds-border)'}`,
            background: formatPath === 'own' ? 'rgba(0,65,194,0.07)' : 'var(--ds-card)',
            boxShadow: formatPath === 'own' ? '0 4px 14px rgba(0,65,194,0.18)' : 'var(--ds-shadow-md)' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <i className="fas fa-building" style={{ color: formatPath === 'own' ? '#0041C2' : 'var(--ds-text2)', fontSize: 14 }} />
            <span style={{ fontWeight: 700, fontSize: 13, color: formatPath === 'own' ? '#0041C2' : 'var(--ds-text)' }}>Attach Your Own Resume Template</span>
            {formatPath === 'own' && <i className="fas fa-check-circle" style={{ marginLeft: 'auto', color: '#16a34a', fontSize: 13 }} />}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--ds-text2)' }}>Upload a company sample resume and extract its formatting template.</div>
        </div>
      </div>
        </div>

        {/* Step 2 — template selection */}
        {formatPath === 'existing' && (
        <div ref={templateDropdownRef}>
          <label style={{ display: 'block', fontWeight: 700, fontSize: 12, color: 'var(--ds-text)', marginBottom: 6 }}>Resume Template</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
                  <button
                    type="button"
                    onClick={() => setTemplateDropdownOpen((o) => !o)}
                    style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '9px 12px', borderRadius: 8, border: `1.5px solid ${templateDropdownOpen ? '#1A5FD4' : 'var(--ds-border)'}`, background: 'var(--ds-input-bg)', color: 'var(--ds-text)', fontSize: 13, fontFamily: 'inherit', cursor: 'pointer', outline: 'none', boxSizing: 'border-box' }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      <span style={{ fontWeight: 700, fontSize: 13, color: '#0041C2' }}>{selectedTemplate ? selectedTemplate.display_name : 'Select a template'}</span>
                      {selectedTemplate && <i className="fas fa-check-circle" style={{ fontSize: 12, color: '#16a34a', flexShrink: 0 }} />}
                    </span>
                    <i className="fas fa-chevron-down" style={{ fontSize: 11, color: 'var(--ds-text2)', transform: templateDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
                  </button>
                  {templateDropdownOpen && (
                    <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 50, background: 'var(--ds-card)', border: '1px solid var(--ds-border)', borderRadius: 8, boxShadow: 'var(--ds-shadow-md)', overflowY: 'auto', maxHeight: 320 }}>
                      {templates.map((t) => {
                        const selected = selectedTemplateId === t.id;
                        return (
                          <div
                            key={t.id}
                            onClick={() => { setSelectedTemplateId(t.id); setTemplateDropdownOpen(false); }}
                            onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = 'var(--ds-input-bg)'; }}
                            onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = 'transparent'; }}
                            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', cursor: 'pointer', background: selected ? 'rgba(0,65,194,0.07)' : 'transparent', borderBottom: '1px solid var(--ds-border)' }}
                          >
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 700, fontSize: 12.5, color: selected ? '#0041C2' : 'var(--ds-text)' }}>
                                {t.display_name}{selected && <i className="fas fa-check-circle" style={{ marginLeft: 6, fontSize: 11, color: '#16a34a' }} />}
                              </div>
                              <div style={{ fontSize: 10.5, color: 'var(--ds-text2)' }}>{t.description}</div>
                            </div>
                            <button onClick={(e) => { e.stopPropagation(); setPreviewTemplateId(t.id); setTemplateDropdownOpen(false); }} title="Preview template" style={{ padding: '4px 8px', borderRadius: 6, cursor: 'pointer', background: 'var(--ds-card)', color: '#1A5FD4', border: '1px solid var(--ds-border)', fontSize: 11, outline: 'none', flexShrink: 0 }}>
                              <i className="fas fa-eye" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <button onClick={() => setPreviewTemplateId(selectedTemplateId)} disabled={!selectedTemplate} title="Preview selected template" style={{ padding: '8px 11px', borderRadius: 8, cursor: selectedTemplate ? 'pointer' : 'not-allowed', background: 'var(--ds-card)', color: '#1A5FD4', border: '1px solid var(--ds-border)', fontSize: 13, outline: 'none' }}>
                  <i className="fas fa-eye" />
                </button>
              </div>
        </div>
        )}

        {/* Step 2 — own template selection */}
        {formatPath === 'own' && (
          <OwnTemplateFlow embedded onTemplateSelect={setCompanyTemplate} />
        )}

        <div style={{ borderTop: '1px solid var(--ds-border)', margin: '16px 0' }} />

        {/* Step 3 — employee resumes & inline batch status */}
          {/* Supported formats hint */}
          <div style={{ fontSize: 12, color: 'var(--ds-text2)', marginBottom: 12 }}>
            <span style={{ fontWeight: 600 }}>Supported file formats:</span> PDF, DOC, DOCX
          </div>

          {/* File input (multi-select) */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontWeight: 700, fontSize: 12, color: 'var(--ds-text)', marginBottom: 6 }}>Select Resume Files</label>
            <input
              type="file"
              multiple
              ref={fileInputRef}
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--ds-border)', background: 'var(--ds-input-bg)', color: 'var(--ds-text)', fontSize: 13, fontFamily: 'inherit', cursor: 'pointer', boxSizing: 'border-box', outline: 'none' }}
            />
            {(batchItems.length > 0 || selectedFiles.length > 0) && (
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {batchItems.length > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                    <span style={{ fontWeight: 700, fontSize: 12, color: 'var(--ds-text)' }}>Resume Formatting</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: '#1A5FD4' }}>
                      {batchItems.filter((x) => x.status === 'completed').length}/{batchItems.length} done
                    </span>
                  </div>
                )}
                {(batchItems.length > 0
                  ? batchItems
                  : selectedFiles.map((file) => ({ file, status: null }))
                ).map((row, idx) => {
                  if (batchItems.length > 0) {
                    const item = row;
                    const meta = {
                      queued: { icon: 'far fa-circle', color: 'var(--ds-text2)', label: 'Queued' },
                      processing: { icon: 'fas fa-sync-alt fa-spin', color: '#1A5FD4', label: 'Processing' },
                      completed: { icon: 'fas fa-check-circle', color: '#16a34a', label: 'Completed' },
                      failed: { icon: 'fas fa-times-circle', color: '#dc2626', label: 'Failed' },
                    }[item.status] || { icon: 'far fa-circle', color: 'var(--ds-text2)', label: 'Queued' };
                    return (
                      <div
                        key={item.id}
                        style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${item.status === 'failed' ? 'rgba(220,38,38,0.35)' : item.status === 'completed' ? 'rgba(34,197,94,0.3)' : 'var(--ds-border)'}`, background: item.status === 'failed' ? 'rgba(220,38,38,0.05)' : item.status === 'completed' ? 'rgba(34,197,94,0.08)' : 'var(--ds-input-bg)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}
                      >
                        <i className={meta.icon} style={{ color: meta.color, fontSize: 13, flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 140 }}>
                          <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--ds-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.file.name}</div>
                          <div style={{ fontSize: 11, color: item.status === 'failed' ? '#dc2626' : meta.color, fontWeight: item.status === 'failed' ? 600 : 400 }}>
                            {item.status === 'processing' && formatStage ? formatStage : meta.label}{item.error ? ` — ${item.error}` : ''}
                          </div>
                        </div>
                        {item.status === 'completed' && (
                          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                            <button onClick={() => openItemPreview(item)} title="Preview formatted resume" style={{ padding: '4px 10px', borderRadius: 6, cursor: 'pointer', background: 'var(--ds-card)', color: '#1A5FD4', border: '1px solid var(--ds-border)', fontSize: 11, fontWeight: 600, fontFamily: 'inherit', outline: 'none' }}>
                              <i className="fas fa-eye" /> Preview
                            </button>
                            <button onClick={() => handleDownloadWord(item)} disabled={downloadingWord || downloadingPdf} title="Download as Word document" style={{ padding: '4px 10px', borderRadius: 6, cursor: 'pointer', background: 'var(--ds-card)', color: '#1A5FD4', border: '1px solid var(--ds-border)', fontSize: 11, fontWeight: 600, fontFamily: 'inherit', outline: 'none', opacity: (downloadingWord || downloadingPdf) ? 0.5 : 1 }}>
                              <i className="fas fa-file-word" /> Download Word
                            </button>
                            <button onClick={() => handleDownloadPDF(item)} disabled={downloadingWord || downloadingPdf} title="Download as PDF" style={{ padding: '4px 10px', borderRadius: 6, cursor: 'pointer', background: 'var(--ds-card)', color: '#1A5FD4', border: '1px solid var(--ds-border)', fontSize: 11, fontWeight: 600, fontFamily: 'inherit', outline: 'none', opacity: (downloadingWord || downloadingPdf) ? 0.5 : 1 }}>
                              <i className="fas fa-file-pdf" /> Download PDF
                            </button>
                          </div>
                        )}
                        {item.status === 'failed' && (
                          <button onClick={() => handleRetryItem(item.id)} disabled={batchRunning} title="Retry this file" style={{ padding: '4px 10px', borderRadius: 6, cursor: batchRunning ? 'not-allowed' : 'pointer', background: 'var(--ds-card)', color: '#dc2626', border: '1px solid #dc2626', fontSize: 11, fontWeight: 600, fontFamily: 'inherit', outline: 'none', opacity: batchRunning ? 0.5 : 1 }}>
                            <i className="fas fa-redo" /> Retry
                          </button>
                        )}
                      </div>
                    );
                  }
                  const file = row.file;
                  return (
                    <div key={`${file.name}-${file.size}-${idx}`} style={{ padding: '7px 12px', borderRadius: 8, background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <i className="fas fa-check" style={{ color: '#16a34a', fontSize: 11, flexShrink: 0 }} />
                        <i className="fas fa-file-alt" style={{ color: '#1A5FD4', fontSize: 13, flexShrink: 0 }} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--ds-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</div>
                          <div style={{ fontSize: 11, color: 'var(--ds-text2)' }}>{(file.size / 1024 / 1024).toFixed(2)} MB</div>
                        </div>
                      </div>
                      <button onClick={() => handleRemoveSelectedFile(idx)} disabled={batchRunning} title="Remove file" style={{ padding: '3px 10px', borderRadius: 6, border: '1.5px solid #dc2626', background: 'transparent', color: '#dc2626', fontSize: 11, fontWeight: 600, cursor: batchRunning ? 'not-allowed' : 'pointer', fontFamily: 'inherit', outline: 'none', opacity: batchRunning ? 0.5 : 1 }}>
                        <i className="fas fa-times me-1" />Remove
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Format button */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <button
              onClick={handleFormatResume}
              disabled={!selectedFiles.length || batchRunning || !hasTemplateForPath}
              style={{ ...footBtn('linear-gradient(135deg,#0041C2,#1A5FD4)', '0 4px 14px rgba(0,65,194,0.35)'), opacity: (!selectedFiles.length || batchRunning || !hasTemplateForPath) ? 0.6 : 1, padding: '9px 24px', fontSize: 13 }}
            >
              {batchRunning ? (
                <><span className="spinner-border spinner-border-sm" role="status" />&nbsp;Formatting...</>
              ) : (
                <><i className="fas fa-magic" />{selectedFiles.length > 1 ? ` Format ${selectedFiles.length} Resumes` : ' Format Resume'}</>
              )}
            </button>
            {batchRunning && formatStage && (
              <div style={{ marginTop: 8, fontSize: 11, color: '#1A5FD4', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 500 }}>
                <span className="spinner-grow spinner-grow-sm" role="status" style={{ width: 7, height: 7 }} />
                <span>{formatStage}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Template preview modal (Feuji1/2/3) ─────────────────────────────── */}
      {previewTemplateId && (
        <TemplatePreviewModal
          template={availableTemplates.find((t) => t.id === previewTemplateId)
            || TEMPLATE_FALLBACK[previewTemplateId]}
          onClose={() => setPreviewTemplateId(null)}
        />
      )}

      {/* ── Formatted Resume Modal ──────────────────────────────────────────── */}
      {showFormattedModal && createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px 12px', paddingLeft: 'calc(var(--app-sidebar-width, 0px) + 12px)', overflowY: 'auto' }}
          onClick={(e) => { if (e.target === e.currentTarget) handleCloseFormattedModal(); }}>
          <div style={{ background: 'rgba(0,0,0,0.55)', position: 'fixed', inset: 0 }} onClick={handleCloseFormattedModal} />
          <div style={{ width: '100%', maxWidth: 1200, background: 'var(--ds-card)', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.4)', overflow: 'hidden', position: 'relative' }}>

            {/* Modal header */}
            <div style={{ padding: '12px 16px', background: 'linear-gradient(135deg,#0041C2,#1A5FD4)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className="fas fa-file-alt" style={{ color: '#fff', fontSize: 13 }} />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#fff' }}>{activeItem?.kind === 'company' ? 'Standardized Resume' : 'Formatted Resume'}</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)' }}>Preview and download your formatted resume</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {!isEditMode && activeItem?.kind !== 'company' && (
                  <button onClick={handleEditMode} style={{ padding: '4px 12px', borderRadius: 6, border: 'none', background: '#22c55e', color: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', outline: 'none' }}>
                    <i className="fas fa-edit me-1" />Edit
                  </button>
                )}
                <button onClick={handleCloseFormattedModal} style={{ width: 28, height: 28, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.2)', color: '#fff', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', outline: 'none' }}>
                  ×
                </button>
              </div>
            </div>

            {/* Modal body */}
            <div style={{ maxHeight: '80vh', overflowY: 'auto', padding: 16, background: 'var(--ds-input-bg)' }}>

              {/* Edit toolbar */}
              {isEditMode && (
                <div style={{ background: 'var(--ds-card)', border: '1px solid var(--ds-border)', borderRadius: 8, padding: '10px 12px', marginBottom: 12, boxShadow: 'var(--ds-shadow-md)' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
                    <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--ds-text)', whiteSpace: 'nowrap' }}>Font:</label>
                    <select value={fontFamily} onChange={handleFontFamilyChange} style={{ width: 140, fontSize: 11, padding: '2px 6px', borderRadius: 5, border: '1.5px solid var(--ds-border)', background: 'var(--ds-input-bg)', color: 'var(--ds-text)', fontFamily: 'inherit', outline: 'none' }}>
                      <option value="Arial, sans-serif">Arial</option>
                      <option value="Times New Roman, serif">Times New Roman</option>
                      <option value="Courier New, monospace">Courier New</option>
                      <option value="Georgia, serif">Georgia</option>
                      <option value="Verdana, sans-serif">Verdana</option>
                      <option value="Helvetica, sans-serif">Helvetica</option>
                      <option value="Calibri, sans-serif">Calibri</option>
                    </select>
                    <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--ds-text)', whiteSpace: 'nowrap' }}>Size:</label>
                    <select value={fontSize} onChange={handleFontSizeChange} style={{ width: 90, fontSize: 11, padding: '2px 6px', borderRadius: 5, border: '1.5px solid var(--ds-border)', background: 'var(--ds-input-bg)', color: 'var(--ds-text)', fontFamily: 'inherit', outline: 'none' }}>
                      {['8px','9px','10px','11px','12px','14px','16px','18px','20px','24px'].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--ds-text)', whiteSpace: 'nowrap' }}>Line Spacing:</label>
                    <select
                      value={lineSpacing}
                      onMouseDown={captureEditorSelection}
                      onChange={handleLineSpacingChange}
                      style={{ width: 72, fontSize: 11, padding: '2px 6px', borderRadius: 5, border: '1.5px solid var(--ds-border)', background: 'var(--ds-input-bg)', color: 'var(--ds-text)', fontFamily: 'inherit', outline: 'none' }}
                      title="Line Spacing"
                    >
                      {LINE_SPACING_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <span style={{ width: 1, height: 20, background: 'var(--ds-border)', margin: '0 2px' }} />
                    <button style={tbBtn(isBold)} onClick={handleToggleBold} title="Bold"><i className="fas fa-bold" /></button>
                    <button style={tbBtn(isItalic)} onClick={handleToggleItalic} title="Italic"><i className="fas fa-italic" /></button>
                    <button style={tbBtn(isUnderline)} onClick={handleToggleUnderline} title="Underline"><i className="fas fa-underline" /></button>
                    <button style={tbBtn(isStrikethrough)} onClick={handleToggleStrikethrough} title="Strikethrough"><i className="fas fa-strikethrough" /></button>
                    <button style={tbBtn(false)} onClick={handleSubscript} title="Subscript"><i className="fas fa-subscript" /></button>
                    <button style={tbBtn(false)} onClick={handleSuperscript} title="Superscript"><i className="fas fa-superscript" /></button>
                    <span style={{ width: 1, height: 20, background: 'var(--ds-border)', margin: '0 2px' }} />
                    <button style={tbBtn(false)} onClick={handleInsertUnorderedList} title="Bullet List"><i className="fas fa-list-ul" /></button>
                    <button style={tbBtn(false)} onClick={handleInsertOrderedList} title="Numbered List"><i className="fas fa-list-ol" /></button>
                    <button style={tbBtn(false)} onClick={handleInsertBullet} title="Insert Bullet">•</button>
                    <button style={tbBtn(false)} onClick={handleIndent} title="Increase Indent"><i className="fas fa-indent" /></button>
                    <button style={tbBtn(false)} onClick={handleOutdent} title="Decrease Indent"><i className="fas fa-outdent" /></button>
                    <span style={{ width: 1, height: 20, background: 'var(--ds-border)', margin: '0 2px' }} />
                    <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--ds-text)', whiteSpace: 'nowrap' }}>Text:</label>
                    <input type="color" value={textColorPicker} onMouseDown={captureEditorSelection} onChange={handleFontColorChange} style={{ width: 36, height: 26, border: '1.5px solid var(--ds-border)', borderRadius: 4, cursor: 'pointer', padding: 1 }} title="Text Color" />
                    <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--ds-text)', whiteSpace: 'nowrap' }}>Bg:</label>
                    <input type="color" value={bgColorPicker} onChange={handleBackgroundColorChange} style={{ width: 36, height: 26, border: '1.5px solid var(--ds-border)', borderRadius: 4, cursor: 'pointer', padding: 1 }} title="Resume page background" />
                    <span style={{ width: 1, height: 20, background: 'var(--ds-border)', margin: '0 2px' }} />
                    <button style={tbBtn(false)} onClick={handleAlignLeft} title="Align Left"><i className="fas fa-align-left" /></button>
                    <button style={tbBtn(false)} onClick={handleAlignCenter} title="Align Center"><i className="fas fa-align-center" /></button>
                    <button style={tbBtn(false)} onClick={handleAlignRight} title="Align Right"><i className="fas fa-align-right" /></button>
                    <button style={tbBtn(false)} onClick={handleAlignJustify} title="Justify"><i className="fas fa-align-justify" /></button>
                    <span style={{ width: 1, height: 20, background: 'var(--ds-border)', margin: '0 2px' }} />
                    <button style={{ ...tbBtn(false), borderColor: '#dc2626', color: '#dc2626' }} onClick={handleClearFormatting} title="Clear Formatting"><i className="fas fa-eraser me-1" />Clear</button>
                  </div>
                </div>
              )}

              {/* Resume preview / editor */}
              {formattedHtml && (
                !isEditMode ? (
                  <div style={{ border: '1px solid var(--ds-border)', borderRadius: 8, background: '#ffffff', overflow: 'hidden' }}>
                    <iframe
                      key={`resume-preview-${previewVersion}`}
                      title="Formatted resume preview"
                      srcDoc={previewSrcDoc}
                      style={{ width: '100%', height: '70vh', border: 'none', display: 'block', background: '#ffffff' }}
                      sandbox="allow-same-origin allow-scripts"
                    />
                  </div>
                ) : (
                  <div style={{ border: '1px solid var(--ds-border)', borderRadius: 8, background: '#ffffff', overflow: 'hidden' }}>
                    <style>{`.resume-formatter-editable:focus,.resume-formatter-editable:focus-visible{outline:none!important;box-shadow:none!important;}`}</style>
                    <div
                      key={`resume-editor-${previewVersion}`}
                      ref={editableContentRef}
                      contentEditable={true}
                      suppressContentEditableWarning={true}
                      className="resume-formatter-editable"
                      style={{ border: 'none', borderRadius: 0, padding: 0, margin: 0, backgroundColor: '#ffffff', boxShadow: 'none', outline: 'none', minHeight: '70vh', width: '100%', display: 'block' }}
                      onBlur={() => {
                        if (editableContentRef.current && isEditMode) {
                          fixExperienceSummaryFieldLayout(
                            editableContentRef.current.querySelector('.resume-container') ||
                              editableContentRef.current
                          );
                        }
                      }}
                      onKeyDown={(e) => {
                        // Work/Project Summary: Enter = plain new line (like Technical Skills
                        // empty bullet line). Do NOT insert a Label:Value / colon row.
                        if (e.key !== 'Enter' || e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
                        const sel = window.getSelection();
                        if (!sel || !sel.rangeCount) return;
                        const anchor = sel.anchorNode;
                        const el =
                          anchor && anchor.nodeType === Node.ELEMENT_NODE
                            ? anchor
                            : anchor?.parentElement;
                        if (!el) return;
                        const li = el.closest?.('.experience-item ul.exp-field-list > li');
                        if (!li || !editableContentRef.current?.contains(li)) return;

                        e.preventDefault();
                        const newLi = document.createElement('li');
                        newLi.appendChild(document.createElement('br'));
                        li.after(newLi);

                        const range = document.createRange();
                        range.setStart(newLi, 0);
                        range.collapse(true);
                        sel.removeAllRanges();
                        sel.addRange(range);
                      }}
                    />
                  </div>
                )
              )}
            </div>

            {/* Modal footer */}
            <div style={{ padding: '12px 16px', background: 'var(--ds-card)', borderTop: '1px solid var(--ds-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              {!isEditMode ? (
                <>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={handleDownloadWord} disabled={downloadingWord || downloadingPdf} style={{ ...footBtn('linear-gradient(135deg,#0041C2,#1A5FD4)', '0 2px 8px rgba(0,65,194,0.3)'), opacity: (downloadingWord || downloadingPdf) ? 0.6 : 1 }}>
                      <i className="fas fa-file-word" /> Download Word
                    </button>
                    <button onClick={handleDownloadPDF} disabled={downloadingWord || downloadingPdf} style={{ ...footBtn('linear-gradient(135deg,#0041C2,#1A5FD4)', '0 2px 8px rgba(0,65,194,0.3)'), opacity: (downloadingWord || downloadingPdf) ? 0.6 : 1 }}>
                      <i className="fas fa-file-pdf" /> Download PDF
                    </button>
                  </div>
                  <button onClick={handleCloseFormattedModal} style={{ padding: '7px 14px', borderRadius: 8, border: '1.5px solid var(--ds-border)', background: 'var(--ds-input-bg)', color: 'var(--ds-text2)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', outline: 'none' }}>
                    Close
                  </button>
                </>
              ) : (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={handleSaveEdit} style={footBtn('#22c55e', '0 2px 8px rgba(34,197,94,0.3)')}>
                    <i className="fas fa-save" /> Save Changes
                  </button>
                  <button onClick={handleCancelEdit} style={footBtn('#f59e0b', '0 2px 8px rgba(245,158,11,0.3)')}>
                    <i className="fas fa-times" /> Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ResumeFormatter;
